/* /assets/art-wall-designer.js
 * Art Wall Designer — page-scoped custom element.
 * Loaded by sections/section-art-wall-designer.liquid.
 */
(function () {
  if (window.customElements && customElements.get('art-wall-designer')) {
    return;
  }

  var LS_FAVS = 'awd:favourites';
  var LS_FAVS_MIGRATED = 'awd:favourites-migrated'; // set once local hearts were pushed to the wishlist
  var LS_FAV_CARDS = 'awd:fav-cards';
  var LS_WALL = 'awd:wall';
  var LS_BG = 'awd:background';

  // Per-size frame-fit corrections for the wall preview. The frame mockup PNGs
  // don't all align perfectly to the print box at every size, so a CSS
  // transform nudges the frame to hug the artwork. The value is a raw
  // `transform` value (e.g. a non-uniform 'scaleX(1) scaleY(1.12)' to stretch
  // only the long rails without widening the print). Keyed by normalized "WxH"
  // in cm (smaller dimension first), and applied in the frame mockup's own
  // (portrait-authored) coordinate space, so a single entry is correct for both
  // portrait and landscape. Any size not listed gets no correction. Extend as
  // needed.
  var FRAME_SIZE_TRANSFORM = {
    '70x100': 'scaleX(1) scaleY(0.97)',
  };

  // Per-size print-fit corrections for the wall preview. The print PNGs share
  // one baked-in aspect ratio (artwork + paper border), but each size's preview
  // box uses that size's real cm aspect. 40x50 (0.80) and 30x40 (0.75) are
  // "squarer" than the PNGs are tall, so the default `object-fit: contain` (see
  // the `.awd__poster__pad > img` rule in art-wall-designer.css) letterboxes
  // the print and leaves a gap on the left/right inside the matt window and
  // frame. For those sizes we scale the print up a hair so it fills the box
  // width (the extra top/bottom is clipped by the pad — see `.awd__poster__pad`
  // overflow below — matching the production site, which renders these a touch
  // shorter). The value is a uniform scale factor (1 = no change); nudge it up
  // until the side gap closes for that size. Keyed by normalized "WxH" in cm
  // (smaller dimension first) so one entry covers both orientations. Any size
  // not listed gets no scale (the CSS `contain` default).
  var PRINT_SIZE_SCALE = {
    '30x40': 1.05,
    '40x50': 1.1,
  };

  // Three placeholder room scenes encoded as inline SVG data URIs so the
  // designer is fully self-contained (no external image dependencies).
  // Each scene mimics: warm wall + parquet floor in different tones.
  function svgScene(wall, floor, accent) {
    var svg =
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 800" preserveAspectRatio="xMidYMid slice">' +
      '<rect width="1200" height="560" fill="' + wall + '"/>' +
      '<rect y="540" width="1200" height="20" fill="' + accent + '" opacity="0.4"/>' +
      '<rect y="560" width="1200" height="240" fill="' + floor + '"/>' +
      // parquet planks
      '<g opacity="0.18" fill="' + accent + '">' +
      '<rect x="0"   y="580" width="160" height="6"/>' +
      '<rect x="180" y="580" width="160" height="6"/>' +
      '<rect x="360" y="580" width="160" height="6"/>' +
      '<rect x="540" y="580" width="160" height="6"/>' +
      '<rect x="720" y="580" width="160" height="6"/>' +
      '<rect x="900" y="580" width="160" height="6"/>' +
      '<rect x="60"  y="640" width="160" height="6"/>' +
      '<rect x="240" y="640" width="160" height="6"/>' +
      '<rect x="420" y="640" width="160" height="6"/>' +
      '<rect x="600" y="640" width="160" height="6"/>' +
      '<rect x="780" y="640" width="160" height="6"/>' +
      '<rect x="960" y="640" width="160" height="6"/>' +
      '<rect x="0"   y="700" width="160" height="6"/>' +
      '<rect x="180" y="700" width="160" height="6"/>' +
      '<rect x="360" y="700" width="160" height="6"/>' +
      '<rect x="540" y="700" width="160" height="6"/>' +
      '<rect x="720" y="700" width="160" height="6"/>' +
      '<rect x="900" y="700" width="160" height="6"/>' +
      '</g>' +
      '</svg>';
    return 'data:image/svg+xml;utf8,' + encodeURIComponent(svg);
  }

  // Passe-partout matt board colours, keyed by colour handle. These are
  // the realistic board colours used for the generated matt — softer
  // than the bright picker-swatch colours in assets/swatches.css. Add
  // the client's real hex per colour here; any colour not listed falls
  // back to its resolved picker-swatch colour.
  var MATT_COLORS = {
    'pink': '#d9beb7',
    'white': '#fbfbfb',
    'light-blue': '#9ec0d4',
    'grey': '#c7c7c1',
    'yellow': '#f9d976',
    'petrol': '#3c5e68',
    'lavender': '#d2cad7',
    'plum': '#5b3f44',
    'black': '#282828',
    'red': '#ac2c3c',
    'blue': '#1d436c',
    'orange': '#d77944',
    'green': '#2d5d42',
  };

  // Build a passe-partout matt as an inline SVG data URI: a solid-colour
  // board (`color`) filling w×h with a rectangular transparent WINDOW cut
  // out so the print shows through. The window sits at (padX,padY) and is
  // shrunk by `overlap` on every side so the matt laps over the print's
  // edge (a realistic rebate) instead of meeting it flush. Rendered as an
  // <img>, the transparent window lets the CSS `drop-shadow` on
  // `.awd__poster__pass` fall onto the print behind it.
  function svgMatt(color, w, h, padX, padY, overlap) {
    var W = Math.max(1, Math.round(w));
    var H = Math.max(1, Math.round(h));
    var wx = Math.round(padX + overlap);
    var wy = Math.round(padY + overlap);
    var wr = Math.round(W - padX - overlap); // window right edge
    var wb = Math.round(H - padY - overlap); // window bottom edge
    // Guard against an inverted window on tiny/edge cases.
    if (wr <= wx) { wx = Math.round(W / 2) - 1; wr = wx + 2; }
    if (wb <= wy) { wy = Math.round(H / 2) - 1; wb = wy + 2; }
    // Outer rectangle + inner (window) rectangle in one path; the
    // even-odd fill rule turns the inner rectangle into a hole.
    var d =
      'M0 0 H' + W + ' V' + H + ' H0 Z ' +
      'M' + wx + ' ' + wy + ' H' + wr + ' V' + wb + ' H' + wx + ' Z';
    var svg =
      '<svg xmlns="http://www.w3.org/2000/svg" width="' + W + '" height="' + H + '" ' +
      'viewBox="0 0 ' + W + ' ' + H + '" preserveAspectRatio="none">' +
      '<path fill="' + color + '" fill-rule="evenodd" d="' + d + '"/>' +
      '</svg>';
    return 'data:image/svg+xml;utf8,' + encodeURIComponent(svg);
  }

  var SCENES = [
    { id: 'scene-1', label: 'Warm white', url: svgScene('#F4F0EA', '#C9A87A', '#6B4A2A') },
    { id: 'scene-2', label: 'Cool gray',  url: svgScene('#EFEFEF', '#B89C82', '#5C4632') },
    { id: 'scene-3', label: 'Sage',       url: svgScene('#E6E8DF', '#A8855F', '#4B3A22') },
  ];

  // If the merchant has added "Background" blocks (or filled out
  // the legacy bg_image_N settings), the section emits a JSON
  // <script data-awd-backgrounds> with the list. We read it here
  // and fall back to the built-in SVG scenes when no entries are
  // present.
  function readScenes(host) {
    var out = [];
    var node = host.querySelector('script[data-awd-backgrounds]');
    if (node && node.textContent) {
      try {
        var parsed = JSON.parse(node.textContent);
        if (Array.isArray(parsed)) {
          parsed.forEach(function (entry) {
            if (!entry || !entry.url) return;
            out.push({
              id: entry.id || ('bg-' + out.length),
              label: entry.label || ('Background ' + (out.length + 1)),
              url: entry.url,
            });
          });
        }
      } catch (e) {
        // ignore — fall through to defaults below
      }
    }
    if (out.length === 0) {
      // Built-in placeholders so the page never has zero scenes.
      return SCENES.slice();
    }
    return out;
  }

  function readJSON(key, fallback) {
    try {
      var raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : fallback;
    } catch (e) {
      return fallback;
    }
  }

  function writeJSON(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* quota / privacy mode — ignore */
    }
  }

  class ArtWallDesigner extends HTMLElement {
    constructor() {
      super();
      this.products = [];
      this.scenes = SCENES;
      this.state = {
        tab: 'all', // 'all' | 'favourites'
        productType: 'art-print', // 'art-print' | 'canvas' | 'wall-object'
        // Currently active collection feeding the grid. Each type
        // tab is backed by its own collection (configured on the
        // [data-awd-type-tab] button via data-awd-collection-handle);
        // switching tabs swaps this and re-fetches via _fetchProducts
        // instead of client-side filtering. Defaults to whatever the
        // server rendered the page with — see _hydrateState().
        collectionHandle: null,
        favourites: new Set(readJSON(LS_FAVS, []) || []),
        wall: readJSON(LS_WALL, []) || [], // [{ uid, id, x, y, scale, z }]
        backgroundId: localStorage.getItem(LS_BG) || SCENES[0].id,
        selectedUid: null,
      };
      this.formatter = null;
      this._uidCounter = 0;
      // Caches keyed by product id. _productCache holds the
      // hydrated data object; _cardCache holds the original
      // server-rendered <li> DOM node so the Favourites tab can
      // re-display products that aren't in the current filter
      // view by cloning the cached node (no string-built HTML).
      this._productCache = Object.create(null);
      this._cardCache = Object.create(null);
    }

    connectedCallback() {
      document.body.classList.add('art-wall-designer-active');
      this.scenes = readScenes(this);
      // Seed the active collection handle from the server-rendered
      // dataset so an early _setTypeTab('art-print') doesn't see a
      // null state.collectionHandle and trigger a redundant fetch
      // of the collection that's already on screen.
      if (!this.state.collectionHandle) {
        this.state.collectionHandle = this.dataset.collectionHandle || null;
      }
      // Re-resolve backgroundId in case the LS value points to a stale id
      // (or pick the first available scene).
      var match = this.scenes.find((s) => s.id === this.state.backgroundId);
      if (!match) this.state.backgroundId = this.scenes[0].id;
      this._cacheNodes();
      this._bind();
      this._renderFavCount();
      this._applyBackground();
      this._loadConfig();
      this._initWishlistBridge();

      // Re-render the wall when the canvas resizes so posters keep
      // their real-world scale relative to the visible room.
      if (window.ResizeObserver) {
        this._resizeObserver = new ResizeObserver(() => {
          if (this.products && this.products.length) this._renderWall();
        });
        if (this.$canvas) this._resizeObserver.observe(this.$canvas);
      }
    }

    disconnectedCallback() {
      document.body.classList.remove('art-wall-designer-active');
      this._teardownWishlistBridge();
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
      // Remove the global listeners bound in `_bind()` so that
      // navigating away from the AWD page (or its section being
      // removed via the Section Rendering API) doesn't leave
      // orphaned handlers attached to `window` / `document`.
      if (this._onWindowResize) {
        window.removeEventListener('resize', this._onWindowResize);
        this._onWindowResize = null;
      }
      if (this._onSharePopupKeydown) {
        document.removeEventListener('keydown', this._onSharePopupKeydown);
        this._onSharePopupKeydown = null;
      }
      if (this._onDrawerKeydown) {
        document.removeEventListener('keydown', this._onDrawerKeydown);
        this._onDrawerKeydown = null;
      }
    }

    _cacheNodes() {
      this.$grid = this.querySelector('[data-awd-product-grid]');
      this.$empty = this.querySelector('[data-awd-empty]');
      this.$favCount = this.querySelector('[data-awd-fav-count]');
      this.$filterCount = this.querySelector('[data-awd-filter-count]');
      this.$filtersBtn = this.querySelector('[data-awd-filters-toggle]');
      this.$productsRoot = this.querySelector('[data-awd-products-root]');
      this.$filterForm = this.querySelector('[data-awd-filter-form]');
      this.$subtotal = this.querySelector('[data-awd-subtotal]');
      this.$cta = this.querySelector('[data-awd-cta]');
      this.$tabs = this.querySelectorAll('[data-awd-tab]');
      this.$canvas = this.querySelector('[data-awd-canvas-root]');
      this.$canvasBg = this.querySelector('[data-awd-canvas-bg]');
      this.$canvasStage = this.querySelector('[data-awd-canvas-stage]');
      this.$bgToggle = this.querySelector('[data-awd-bg-toggle]');
      this.$bgModal = this.querySelector('[data-awd-bg-modal]');
      this.$bgModalOverlay = this.querySelector('[data-awd-bg-modal-overlay]');
      this.$bgModalClose = this.querySelector('[data-awd-bg-modal-close]');
      this.$bgModalCards = this.querySelector('[data-awd-bg-modal-cards]');
      this.$bgModalScroller = this.querySelector('[data-awd-bg-modal-scroller]');
      this.$bgModalProgress = this.querySelector('[data-awd-bg-modal-progress]');
      this.$bgModalProgressBar = this.querySelector('[data-awd-bg-modal-progress-bar]');
      this.$bgModalConfirm = this.querySelector('[data-awd-bg-modal-confirm]');
      this.$clear = this.querySelector('[data-awd-clear]');
      this.$sidebar = this.querySelector('[data-awd-sidebar]');
      this.$configure = this.querySelector('[data-awd-configure]');
      this.$configureBody = this.querySelector('[data-awd-configure-body]');
      this.$configureClose = this.querySelector('[data-awd-configure-close]');

      // Shop-this-art-wall drawer
      this.$drawer = this.querySelector('[data-awd-drawer]');
      this.$drawerToggle = this.querySelector('[data-awd-drawer-toggle]');
      this.$drawerClose = this.querySelector('[data-awd-drawer-close]');
      this.$drawerOverlay = this.querySelector('[data-awd-drawer-overlay]');
      this.$drawerRows = this.querySelector('[data-awd-drawer-rows]');
      this.$drawerEmpty = this.querySelector('[data-awd-drawer-empty]');
      this.$drawerSubtotal = this.querySelector('[data-awd-drawer-subtotal]');
      this.$drawerCount = this.querySelector('[data-awd-drawer-count]');
      this.$drawerAdd = this.querySelector('[data-awd-drawer-add]');
      this.$drawerCheckout = this.querySelector('[data-awd-drawer-checkout]');

      // Confirm modal
      this.$confirm = this.querySelector('[data-awd-confirm]');
      this.$confirmTitle = this.querySelector('[data-awd-confirm-title]');
      this.$confirmOk = this.querySelector('[data-awd-confirm-ok]');
      this.$confirmCancel = this.querySelector('[data-awd-confirm-cancel]');
      this.$confirmBackdrop = this.querySelector('[data-awd-confirm-backdrop]');

      // Share popup
      this.$shareBtn = document.querySelector('[data-awd-share]');
      this.$sharePopup = this.querySelector('[data-awd-share-popup]');
      this.$sharePopupOverlay = this.querySelector('[data-awd-share-popup-overlay]');
      this.$sharePopupClose = this.querySelector('[data-awd-share-popup-close]');
      this.$sharePopupPreview = this.querySelector('[data-awd-share-popup-preview-inner]');
      this.$sharePopupToast = this.querySelector('[data-awd-share-popup-toast]');
      this.$shareActionBtns = this.querySelectorAll('[data-awd-share-action]');
    }

    _bind() {
      this.$tabs.forEach((btn) => {
        btn.addEventListener('click', () => this._setTab(btn.dataset.awdTab));
      });
      this.$filtersBtn.addEventListener('click', () => this._toggleFilters());

      // Type tabs (Art Prints / Canvas / Wall Objects). The
      // current product catalog is all "Art Print"s, so the other
      // tabs simply hide everything; the filter logic is here so
      // future product types start filtering automatically.
      this.$typeTabs = this.querySelectorAll('[data-awd-type-tab]');
      this.$typeTabs.forEach((btn) => {
        btn.addEventListener('click', () => this._setTypeTab(btn.dataset.awdTypeTab));
      });

      // Filter panel close / apply buttons live INSIDE the form so
      // they're rebound by `_wireFilterForm()` whenever the form
      // is swapped via the Section Rendering API.

      // Background modal handles its own close via overlay/X.

      // Canvas drag-and-drop is removed in Sprint 06 — sidebar
      // cards now have an explicit ADD TO WALL button instead.

      // Background modal
      this.$bgToggle.addEventListener('click', () => this._toggleBgModal(true));
      if (this.$bgModalClose) this.$bgModalClose.addEventListener('click', () => this._toggleBgModal(false));
      if (this.$bgModalOverlay) this.$bgModalOverlay.addEventListener('click', () => this._toggleBgModal(false));
      if (this.$bgModalConfirm) this.$bgModalConfirm.addEventListener('click', () => this._confirmBgModal());
      if (this.$bgModalScroller) {
        this.$bgModalScroller.addEventListener('scroll', () => this._updateBgModalProgress());
        // Stored on `this` so `disconnectedCallback()` can detach it.
        this._onWindowResize = () => this._updateBgModalProgress();
        window.addEventListener('resize', this._onWindowResize);
      }

      // Clear wall
      this.$clear.addEventListener('click', () => {
        if (this.state.wall.length === 0) return;
        this._confirm('Do you want to clear this Art Wall?').then((ok) => {
          if (!ok) return;
          this.state.wall = [];
          this.state.selectedUid = null;
          writeJSON(LS_WALL, this.state.wall);
          this._renderWall();
          this._updateSubtotal();
          this._closeConfigure();
        });
      });

      // Deselect when clicking empty canvas background.
      this.$canvasStage.addEventListener('pointerdown', (e) => {
        if (e.target === this.$canvasStage && this.state.selectedUid) {
          this.state.selectedUid = null;
          this._renderWall();
        }
      });

      // Configure panel close button
      this.$configureClose.addEventListener('click', () => this._closeConfigure());

      // Configure panel option clicks (delegated)
      this.$configureBody.addEventListener('click', (e) => {
        var btn = e.target.closest('[data-awd-cfg]');
        if (!btn) return;
        e.preventDefault();
        var kind = btn.dataset.awdCfg;
        var value = btn.dataset.awdValue;
        this._applyConfigChange(kind, value);
      });

      // Share popup wiring
      if (this.$shareBtn) {
        this.$shareBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this._openSharePopup();
        });
      }
      if (this.$sharePopupClose) this.$sharePopupClose.addEventListener('click', () => this._closeSharePopup());
      if (this.$sharePopupOverlay) this.$sharePopupOverlay.addEventListener('click', () => this._closeSharePopup());
      if (this.$shareActionBtns && this.$shareActionBtns.forEach) {
        this.$shareActionBtns.forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            this._handleShareAction(btn.dataset.awdShareAction, btn);
          });
        });
      }
      // Stored on `this` so `disconnectedCallback()` can detach it.
      this._onSharePopupKeydown = (e) => {
        if (e.key === 'Escape' && this.$sharePopup && !this.$sharePopup.hasAttribute('hidden')) {
          this._closeSharePopup();
        }
      };
      document.addEventListener('keydown', this._onSharePopupKeydown);

      // Shop-this-art-wall drawer
      if (this.$drawerToggle) {
        this.$drawerToggle.addEventListener('click', (e) => {
          e.preventDefault();
          if (this.$drawerToggle.disabled) return;
          this._toggleDrawer();
        });
      }
      if (this.$drawerClose) {
        this.$drawerClose.addEventListener('click', () => this._toggleDrawer(false));
      }
      if (this.$drawerOverlay) {
        this.$drawerOverlay.addEventListener('click', () => this._toggleDrawer(false));
      }
      if (this.$drawerAdd) {
        this.$drawerAdd.addEventListener('click', () => this._addToBag());
      }
      if (this.$drawerCheckout) {
        this.$drawerCheckout.addEventListener('click', () => this._proceedToCheckout());
      }
      // Stored on `this` so `disconnectedCallback()` can detach it.
      this._onDrawerKeydown = (e) => {
        if (e.key === 'Escape' && this.$drawer && this.$drawer.classList.contains('is-open')) {
          this._toggleDrawer(false);
        }
      };
      document.addEventListener('keydown', this._onDrawerKeydown);
    }

    _hasProductPayload(e) {
      try {
        return Array.from(e.dataTransfer.types || []).indexOf('text/awd-product-id') !== -1;
      } catch (err) {
        return false;
      }
    }

    _loadConfig() {
      this._afterConfigLoaded({});
    }

    _afterConfigLoaded(data) {
      this.currency = data.currency || this.dataset.currency || 'EUR';
      this.locale = data.locale || this.dataset.locale || 'en-IE';
      // Currency symbol for compact price labels (e.g. the filter price range),
      // mirroring the collection filters which use {{ cart.currency.symbol }}.
      this.currencySymbol = this.dataset.currencySymbol || '€';

      // Real Shopify addon variants emitted by the section liquid
      // (from the global print_configuration metaobject). Used to
      // build the bundle items at add-to-cart time and to drive the
      // configure-panel UI (so only addons that actually fit the
      // poster's selected size are offered).
      this.addons = { passepartout: [], frame: [] };
      try {
        var addonsTag = document.querySelector('script[data-awd-addons]');
        if (addonsTag) {
          var parsed = JSON.parse(addonsTag.textContent || '{}');
          this.addons.passepartout = Array.isArray(parsed.passepartout) ? parsed.passepartout : [];
          this.addons.frame = Array.isArray(parsed.frame) ? parsed.frame : [];
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[awd] failed to parse addons JSON', e);
      }

      // Preload every frame variant mockup so that when a user
      // selects a frame on the wall there's no perceptible delay
      // before the image appears (the browser already has it in the
      // image cache). Dedupes by URL.
      try {
        var seenFrameImgs = Object.create(null);
        this.addons.frame.forEach(function (a) {
          var src = a && a.swatch_image;
          if (!src || seenFrameImgs[src]) return;
          seenFrameImgs[src] = true;
          var img = new Image();
          img.decoding = 'async';
          img.src = src;
        });
      } catch (e) { /* preload is best-effort */ }
      try {
        this.formatter = new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
        });
      } catch (e) {
        // A malformed locale tag still shouldn't downgrade the currency:
        // retry with the default locale but keep the resolved currency.
        try {
          this.formatter = new Intl.NumberFormat('en-IE', {
            style: 'currency',
            currency: this.currency,
          });
        } catch (e2) {
          this.formatter = new Intl.NumberFormat('en-IE', {
            style: 'currency',
            currency: 'EUR',
          });
        }
      }
      // Hydrate products from the server-rendered grid, wire the
      // filter form, then render the wall + subtotal.
      this._hydrateProductsFromDom();
      this._hydrateFavouritesFromStorage();
      this._backfillFavouriteSnapshots();
      this._wireGridCards();
      this._wireFilterForm();
      this._initPagination();
      // Hide cards whose variants have no parseable physical size
      // (incompatible with the real-world scale math the wall uses).
      // Run on first paint so e.g. prints without the
      // plytix.width_cm / plytix.height_cm metafields don't appear
      // in the grid only to break when added to the wall. (Wall
      // objects without a "Product Size" option are already skipped
      // server-side by awd-product-card.liquid.)
      this._applyIncompleteFilter();
      this._renderBgModal();

      // If a ?wall=<token> URL param is present, rebuild the wall
      // from the encoded share state (overrides whatever was in
      // localStorage). We render once the products are fetched so
      // the snapshot has full variant data.
      var self = this;
      this._hydrateFromUrl().then(function (loaded) {
        self._renderWall();
        self._updateSubtotal();
        // Re-resolve placed wall objects from their authoritative cards so
        // a saved/shared wall reflects catalog changes (e.g. corrected
        // dimensions) rather than the snapshot frozen at add/share time.
        // Then re-check the restored wall against live availability —
        // sold-out products no longer render grid cards, so a stale
        // localStorage wall is the one place they could otherwise survive.
        Promise.resolve(self._hydrateWallProductData()).then(function () {
          self._validateWallAvailability();
        });
        if (loaded) {
          // Strip the wall param from the URL so refreshes don't
          // keep overriding edits the user makes locally.
          try {
            var u = new URL(window.location.href);
            u.searchParams.delete('wall');
            window.history.replaceState({}, '', u.toString());
          } catch (e) {}
        }
      });

      this._refreshTabState();
      // The inline server render runs in /pages/ context where
      // `collection.filters` is empty — fetch the same section via
      // the Section Rendering API on /collections/<handle> so the
      // filter form gets populated with real Shopify storefront
      // filters (size, price, artist metafield, etc.).
      this._fetchProducts('');
      this.dispatchEvent(
        new CustomEvent('awd:ready', {
          detail: { count: this.products.length },
        })
      );
    }

    // Parse a variant title (e.g. "A4 (21\u00d729.7 cm)", "30\u00d740 cm",
    // "50x70 cm", "70x100 cm") into { width_cm, height_cm }. Returns
    // null if no two numbers can be extracted.
    _parseVariantSize(title) {
      if (!title) return null;
      var t = String(title).replace(',', '.');
      var m = t.match(/(\d+(?:\.\d+)?)\s*[x\u00d7X]\s*(\d+(?:\.\d+)?)/);
      if (!m) return null;
      var w = parseFloat(m[1]);
      var h = parseFloat(m[2]);
      if (!isFinite(w) || !isFinite(h) || w <= 0 || h <= 0) return null;
      return { width_cm: w, height_cm: h };
    }

    _readCardVariants(card) {
      var raw = card.dataset.awdVariants;
      if (!raw) return [];
      var arr;
      try { arr = JSON.parse(raw); } catch (e) { return []; }
      if (!Array.isArray(arr)) return [];
      var self = this;
      return arr.map(function (v) {
        // Prefer the explicit width_cm / height_cm the card emitted —
        // for wall objects these are parsed server-side from the
        // "Product Size" option value (e.g. "25×25×10"); for prints /
        // canvas they come from the plytix.width_cm / plytix.height_cm
        // variant metafields. Fall back to parsing the variant title,
        // and finally to A4 (21×29.7) for legacy products that have
        // neither.
        var mfW = parseFloat(v.width_cm);
        var mfH = parseFloat(v.height_cm);
        var dims;
        if (isFinite(mfW) && mfW > 0 && isFinite(mfH) && mfH > 0) {
          dims = { width_cm: mfW, height_cm: mfH };
        } else {
          dims = self._parseVariantSize(v.size || v.title) || { width_cm: 21, height_cm: 29.7 };
        }
        return {
          id: String(v.id),
          title: v.title || '',
          // Pure size option value (e.g. "A4 (21x29.7 cm)") — emitted
          // server-side by awd-product-card.liquid so multi-option
          // products don't smuggle other option values (like a colour)
          // into the size string used to match add-on variants.
          size: v.size || v.title || '',
          price: parseInt(v.price, 10) || 0,
          available: !!v.available,
          width_cm: dims.width_cm,
          height_cm: dims.height_cm,
          sku: v.sku,
        };
      });
    }

    _hydrateProductsFromDom() {
      // Read every <li data-awd-*> card the section emitted server
      // side and rebuild the in-memory product list. Merges into
      // _productCache so previously seen products survive filter
      // swaps (used by the Favourites tab).
      var cards = this.$grid ? this.$grid.querySelectorAll('[data-awd-product-id]') : [];
      var current = [];
      cards.forEach((card) => {
        var p = this._productFromCard(card);
        if (!p) return;
        this._productCache[p.id] = p;
        // Snapshot a detached clone of the original card so we can
        // re-render it later (favourites view) without rebuilding
        // HTML from string-concat — avoids any XSS risk from
        // titles / artist names containing markup characters.
        this._cardCache[p.id] = card.cloneNode(true);
        current.push(p);
      });
      // Active product list shown in the grid = current rendered set;
      // for everything else (favourites, wall lookup) we fall back to
      // the cache via _productById.
      this.products = current;
      // Snapshot the server-rendered grid HTML so we can restore it
      // when the user switches Favourites → All without losing the
      // current filter view.
      if (this.$grid) this._allGridHTML = this.$grid.innerHTML;
      // Refresh hearts to reflect favourite state.
      this._reflectHeartsState();
    }

    // Build the in-memory product object from a server-rendered card.
    // Shared by the grid hydration and the on-load wall hydration so the
    // wall always resolves dimensions / image / orientation from the same
    // authoritative (server-parsed) source — never a frozen client copy.
    _productFromCard(card) {
      var id = card && card.dataset && card.dataset.awdProductId;
      if (!id) return null;
      return {
        id: id,
        title: card.dataset.awdTitle || '',
        artist: card.dataset.awdArtist || '',
        image: card.dataset.awdImage || '',
        print_image: card.dataset.awdPrintImage || '',
        price: parseInt(card.dataset.awdPriceCents || '0', 10),
        handle: card.dataset.awdHandle || '',
        url: card.dataset.awdUrl || '',
        orientation: (card.dataset.awdOrientation || 'portrait').toLowerCase(),
        productType: card.dataset.awdProductType || '',
        isWallObject: card.dataset.awdIsWallObject === 'true',
        variants: this._readCardVariants(card),
      };
    }

    // The product fields persisted on a wall item so it can render after a
    // reload even if the source product isn't in any loaded grid. Built from
    // a (preferably fresh) product object; kept in one place so the add-to-
    // wall snapshot and the on-load re-hydration stay in sync.
    _snapshotFromProduct(p) {
      return {
        id: p.id,
        title: p.title,
        artist: p.artist,
        image: p.image,
        print_image: p.print_image || '',
        price: p.price,
        handle: p.handle,
        url: p.url,
        orientation: p.orientation || 'portrait',
        productType: p.productType || '',
        isWallObject: !!p.isWallObject,
        variants: (p.variants || []).slice(),
      };
    }

    // Re-resolve wall products from their authoritative server-rendered cards
    // on load, so saved (localStorage) and shared (?wall=) art walls pick up
    // catalog changes — corrected wall-object dimensions, and (critically for
    // Markets) PRICES in the currently-active market — instead of rendering
    // from a frozen snapshot captured in whatever market the item was added.
    //
    // Prints live in the default grid, already re-hydrated in the active market
    // by the _fetchProducts('') call on init, so they're correct. Canvas and
    // wall objects are each backed by their OWN collection (only fetched lazily
    // when their tab is opened), so a wall item of those types otherwise falls
    // back to its snapshot — which, after switching market, still shows the old
    // currency's amount under the new symbol (e.g. the DKK number as "€"). Heal
    // both here by re-fetching their collection (a section render, so it runs
    // in the active market) and overwriting the cached/snapshot price.
    _hydrateWallProductData() {
      var self = this;
      var sectionId = this.dataset.productsSectionId;
      if (!sectionId) return;
      var wall = this.state.wall || [];
      if (!wall.length) return;

      // A wall item needs a refresh when it has no authoritative (non-snapshot)
      // product data hydrated this load — i.e. it would render from its frozen
      // snapshot. Prints resolve from the default grid, so only canvas + wall
      // objects need a dedicated collection fetch.
      function needsRefresh(it) {
        var cached = self._productCache[String(it.id)];
        if (cached && !cached.__fromSnapshot) return false;
        return !!it.product_snapshot;
      }
      // Collection handle backing a type tab, read from the tab button so it
      // stays in sync with the section config (falls back to the known handle).
      function tabCollection(tab, fallback) {
        var btn = self.querySelector('[data-awd-type-tab="' + tab + '"]');
        return (btn && btn.dataset.awdCollectionHandle) || fallback;
      }

      var handles = [];
      if (wall.some(function (it) { return needsRefresh(it) && it.product_snapshot.isWallObject; })) {
        handles.push(tabCollection('wall-object', 'system-awd-wall-objects'));
      }
      if (wall.some(function (it) {
        return needsRefresh(it)
          && !it.product_snapshot.isWallObject
          && /canvas/i.test(it.product_snapshot.productType || '');
      })) {
        handles.push(tabCollection('canvas', 'system-awd-canvas'));
      }
      if (!handles.length) return;

      // Page 1 of each collection. Items beyond the first page still heal when
      // that tab is opened (which fetches the collection in the active market);
      // this covers the common case without extra round-trips.
      return Promise.all(handles.map(function (handle) {
        var url =
          '/collections/' + encodeURIComponent(handle) +
          '?section_id=' + encodeURIComponent(sectionId);
        return fetch(url, { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.text() : null; })
          .then(function (html) {
            if (!html) return;
            var doc = new DOMParser().parseFromString(html, 'text/html');
            doc.querySelectorAll('[data-awd-product-id]').forEach(function (card) {
              var p = self._productFromCard(card);
              // Authoritative server data overrides the snapshot in the cache.
              if (p) self._productCache[String(p.id)] = p;
            });
          })
          .catch(function () { /* keep the snapshot fallback on failure */ });
      })).then(function () {
        // Rewrite the persisted snapshot on each refreshed wall item so the
        // NEXT load starts from current data (no repeated heal/flash), then
        // re-render the wall + subtotal with corrected prices/dimensions.
        var persisted = false;
        (self.state.wall || []).forEach(function (it) {
          var p = self._productCache[String(it.id)];
          if (!p || p.__fromSnapshot) return;
          it.product_snapshot = self._snapshotFromProduct(p);
          persisted = true;
        });
        if (persisted) writeJSON(LS_WALL, self.state.wall);
        self._renderWall();
        self._updateSubtotal();
      });
    }

    // Sold-out products are excluded from the designer, but a wall restored
    // from localStorage (or held across a long session) can still reference
    // products/variants that sold out after they were added — their frozen
    // snapshots claim `available: true` forever, and the OOS product no
    // longer renders a grid card that could correct them. Re-check every
    // snapshot-backed item against live /products/{handle}.js data:
    //   - product deleted/unpublished or every variant sold out → the item
    //     is removed from the wall (it can't be bought at all);
    //   - only the selected variant sold out → move to the default
    //     available size and drop the size-bound add-ons picked for the
    //     old size (canvas re-asserts its mandatory frame).
    // This is what keeps a stale wall from dead-ending in a Shopify 422 at
    // add-to-bag time.
    // Fetch a product's live storefront JSON, respecting the localized
    // root (Markets URL prefixes) and memoizing successful responses per
    // session. Resolves to:
    //   an object  — the parsed product .js payload
    //   null       — the product is GONE (404/410: deleted or unpublished)
    //   undefined  — inconclusive (throttled, 5xx, non-JSON, network
    //                error) — callers must NOT treat this as sold out.
    _fetchProductJson(handle) {
      this._liveProductJson = this._liveProductJson || {};
      var cache = this._liveProductJson;
      if (handle in cache) return Promise.resolve(cache[handle]);
      var root = (window.theme && window.theme.routes && window.theme.routes.root) || '/';
      if (root.charAt(root.length - 1) !== '/') root += '/';
      return fetch(root + 'products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
        .then(function (r) {
          if (r.status === 404 || r.status === 410) return null;
          if (!r.ok) return undefined;
          return r.json();
        })
        .then(function (p) {
          if (p !== undefined) cache[handle] = p;
          return p;
        })
        .catch(function () { return undefined; });
    }

    _validateWallAvailability() {
      var self = this;
      // Single-flight: overlapping calls (init + add-poster) would fetch
      // the same handles concurrently and race on render/persist.
      if (this._validateWallPromise) return this._validateWallPromise;
      var wall = this.state.wall || [];
      if (!wall.length) return Promise.resolve(false);

      // Live data is only needed where we'd otherwise trust a snapshot;
      // products hydrated from the current grid are already current.
      var handles = [];
      wall.forEach(function (it) {
        var cached = self._productCache[String(it.id)];
        var authoritative = cached && !cached.__fromSnapshot;
        var handle = (cached && cached.handle) || (it.product_snapshot && it.product_snapshot.handle);
        if (!authoritative && handle && handles.indexOf(handle) === -1) handles.push(handle);
      });

      var liveByHandle = {};
      this._validateWallPromise = Promise.all(handles.map(function (handle) {
        return self._fetchProductJson(handle).then(function (p) { liveByHandle[handle] = p; });
      })).then(function () {
        // The wall array is mutated in place by add/remove (and by this
        // function), so the captured reference stays current — but Clear
        // and share hydration REPLACE it. If that happened while the
        // fetches were in flight, operating on the captured array would
        // resurrect the old wall; bail out.
        if (self.state.wall !== wall) return false;
        var changed = false;
        var removed = [];
        wall.forEach(function (it) {
          var product = self._productById(it.id);
          if (!product) return;
          var cached = self._productCache[String(it.id)];
          var authoritative = cached && !cached.__fromSnapshot;
          var live = liveByHandle[product.handle];

          // Merge live availability into the snapshot's variant data so
          // every later consumer — size chips, default variant, drawer —
          // sees the current state. The variant objects are shared between
          // the _productById wrapper and the persisted snapshot, so one
          // pass updates both. A live variant list is also authoritative
          // about EXISTENCE: a persisted variant id that is no longer in
          // it is a deleted/recreated variant and must be re-picked.
          var liveIds = null;
          if (!authoritative && live && live.variants) {
            liveIds = {};
            live.variants.forEach(function (v) { liveIds[String(v.id)] = !!v.available; });
            (product.variants || []).forEach(function (v) {
              var key = String(v.id);
              if (key in liveIds && v.available !== liveIds[key]) {
                v.available = liveIds[key];
                changed = true;
              }
            });
          }

          // Product gone from the storefront (404/410) or fully sold
          // out → it is no longer offered in the designer at all.
          // Inconclusive fetches (undefined) never remove anything.
          var gone = !authoritative && live === null;
          var vs = product.variants || [];
          var anyAvailable = vs.some(function (v) { return v.available !== false; });
          if (gone || (vs.length && !anyAvailable)) {
            removed.push(it);
            changed = true;
            return;
          }

          // Selected variant sold out — or deleted from the catalog
          // (id absent from the live variant list) → move to the
          // default available size; _setItemVariant re-picks the
          // frame/passe-partout at the new size where possible.
          var current = self._variantForItem(it, product);
          var variantDeleted = liveIds && it.variant_id != null && !(String(it.variant_id) in liveIds);
          if ((current && current.available === false) || variantDeleted) {
            self._setItemVariant(it, self._defaultVariantId(product));
            if (self._configureUid === it.uid) self._renderConfigure();
            changed = true;
          }
        });
        if (!changed) return false;
        // Remove in place so the array identity the guard above relies
        // on is preserved for any later overlapping run.
        removed.forEach(function (it) {
          var idx = wall.indexOf(it);
          if (idx !== -1) wall.splice(idx, 1);
          if (self.state.selectedUid === it.uid) self.state.selectedUid = null;
          if (self._configureUid === it.uid) self._closeConfigure();
        });
        writeJSON(LS_WALL, self.state.wall);
        self._renderWall();
        self._updateSubtotal();
        return true;
      }).finally(function () {
        self._validateWallPromise = null;
      });
      return this._validateWallPromise;
    }

    // Human-facing product-type label shown in the drawer. Wall objects
    // are flagged server-side (their product.type is unreliable and may
    // even default to "Art Print"), so force the correct label for them;
    // everything else shows its real Shopify product type, falling back
    // to "Art Print" for the legacy print-only catalog.
    _productTypeLabel(p) {
      if (p && p.isWallObject) return 'Wall Object';
      // The print catalog's real Shopify product type is "Print". Some products
      // have a blank type and previously fell back to "Art Print", which is why
      // the bag showed a mix of "Print" and "Art Print". There is no genuine
      // "Art Print" type, so normalise blank/legacy values to "Print" for one
      // consistent label. Other real types (e.g. "Canvas") pass through.
      var t = (p && p.productType) ? String(p.productType).trim() : '';
      var key = t.toLowerCase();
      if (t === '' || key === 'print' || key === 'art print') return 'Print';
      return t;
    }

    _productById(id) {
      var key = String(id);
      var cached = this._productCache[key];
      // A product hydrated from a real grid card is authoritative and always
      // wins, so catalog corrections (dimensions, image, orientation) reach
      // posters placed earlier — including saved & shared art walls. A cached
      // entry flagged `__fromSnapshot` is only a fallback and must NOT shadow
      // fresh data once it arrives.
      if (cached && !cached.__fromSnapshot) return cached;
      var hit = this.products.find((p) => String(p.id) === key);
      if (hit) return hit;
      if (cached) return cached;
      // Final fallback: the snapshot persisted on the wall item. Keeps
      // posters visible after a reload even if their source product isn't
      // in any loaded grid; flagged so a later real hydration overrides it.
      var fromWall = (this.state.wall || []).find((it) => String(it.id) === key && it.product_snapshot);
      if (fromWall) {
        var snap = Object.assign({ __fromSnapshot: true }, fromWall.product_snapshot);
        this._productCache[key] = snap;
        return snap;
      }
      return null;
    }

    /* ───── state setters ───── */

    _setTab(tab) {
      if (tab !== 'all' && tab !== 'favourites') return;
      this.state.tab = tab;
      this.$tabs.forEach((b) => {
        var active = b.dataset.awdTab === tab;
        b.classList.toggle('awd__tab--active', active);
        b.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      this._refreshTabState();
    }

    _setTypeTab(type) {
      if (!type) return;
      // Avoid a no-op tab click triggering a network round-trip when
      // the user clicks the already-active tab.
      var prevType = this.state.productType;
      this.state.productType = type;
      if (this.$typeTabs) {
        this.$typeTabs.forEach((b) => {
          var active = b.dataset.awdTypeTab === type;
          b.classList.toggle('awd__type-tab--active', active);
          b.setAttribute('aria-selected', active ? 'true' : 'false');
        });
      }
      // Each type tab is backed by its own collection (see
      // section-art-wall-designer.liquid). Swap the active handle
      // and re-fetch — much cleaner than client-side filtering
      // across one mega-collection.
      var btn = this.querySelector('[data-awd-type-tab="' + type + '"]');
      var nextHandle = btn && btn.dataset.awdCollectionHandle;
      if (nextHandle && nextHandle !== this.state.collectionHandle) {
        this.state.collectionHandle = nextHandle;
        // Reset any active storefront filters when changing tabs —
        // the filter values won't be valid against the new
        // collection (e.g. a print-only artist on the canvas
        // collection) and the form gets re-rendered by
        // _fetchProducts anyway.
        this.state.filterParams = '';
        this._fetchProducts('');
      } else if (prevType !== type) {
        // Same collection but different active tab — usually means
        // re-paint of tab styles is all that's needed.
        this._applyTypeFilter();
      }
    }

    // A card is "incomplete" (for AWD purposes) when not a single one
    // of its variants exposes a size string that we can parse into
    // physical dimensions. Such a product can't be hung on the wall,
    // so we hide it from the grid entirely.
    _cardHasUsableSize(card) {
      var raw = card && card.dataset && card.dataset.awdVariants;
      if (!raw) return false;
      var arr;
      try { arr = JSON.parse(raw); } catch (e) { return false; }
      if (!Array.isArray(arr) || !arr.length) return false;
      for (var i = 0; i < arr.length; i += 1) {
        var v = arr[i];
        // Explicit width_cm / height_cm emitted by the card take
        // precedence over a parseable size string. Wall objects get
        // these parsed server-side from their "Product Size" option
        // value; prints / canvas from the plytix.width_cm /
        // plytix.height_cm variant metafields.
        var mfW = parseFloat(v && v.width_cm);
        var mfH = parseFloat(v && v.height_cm);
        if (isFinite(mfW) && mfW > 0 && isFinite(mfH) && mfH > 0) return true;
        var sz = (v && (v.size || v.title)) || '';
        if (this._parseVariantSize(sz)) return true;
      }
      return false;
    }

    // Walk the grid and mark/hide cards whose variants don't carry a
    // parseable physical size. Idempotent: cards already marked stay
    // marked. After marking, the pagination count is refreshed and we
    // opportunistically pull the next page if the filter left us with
    // a thin visible set.
    _applyIncompleteFilter() {
      if (!this.$grid) return;
      var cards = this.$grid.querySelectorAll('[data-awd-product-id]');
      var self = this;
      var changed = false;
      cards.forEach(function (card) {
        if (card.classList.contains('awd__card--incomplete')) {
          if (!card.hidden) { card.hidden = true; changed = true; }
          return;
        }
        if (!self._cardHasUsableSize(card)) {
          card.classList.add('awd__card--incomplete');
          card.hidden = true;
          changed = true;
        }
      });
      // Persist filtered state into the grid snapshot so that
      // subsequent Favourites → All restores don't re-show the
      // cards we just hid (and so subsequent load-more pages can
      // still locate the sentinel inside the snapshot).
      if (changed && this.$grid) this._allGridHTML = this.$grid.innerHTML;
      this._updatePaginationCount();
      this._maybeAutoLoadMore();
    }

    // Replace "Showing X of Y" with the count of visible cards (i.e.
    // excluding ones we filtered out client-side). Keeps the figure
    // honest against what the user actually sees.
    _updatePaginationCount() {
      if (!this.$pagination) return;
      var label = this.$pagination.querySelector('[data-awd-pagination-count]');
      if (!label) {
        label = this.$pagination.querySelector('.awd__pagination__count');
      }
      if (!label) return;
      var tmpl = label.getAttribute('data-awd-count-template') || label.textContent || '';
      if (!label.getAttribute('data-awd-count-template')) {
        label.setAttribute('data-awd-count-template', tmpl);
      }
      var visible = 0;
      this.$grid.querySelectorAll('[data-awd-product-id]').forEach(function (card) {
        if (!card.hidden) visible += 1;
      });
      // Replace the first numeric run in the original label with the
      // visible count. Falls back to a simple "X" string when the
      // template doesn't contain a number.
      var next = tmpl.replace(/\d+/, String(visible));
      label.textContent = next;
    }

    // If filtering left the grid showing fewer than this threshold,
    // and another page is available, fetch it. Prevents the user
    // from ever seeing a near-empty grid after the incomplete filter
    // removes most of a page.
    _maybeAutoLoadMore() {
      if (!this.$grid || !this.$pagination) return;
      if (this._paginationLoading) return;
      var current = parseInt(this.$pagination.dataset.currentPage || '1', 10);
      var total = parseInt(this.$pagination.dataset.totalPages || '1', 10);
      if (!current || !total || current >= total) return;
      var visible = 0;
      this.$grid.querySelectorAll('[data-awd-product-id]').forEach(function (card) {
        if (!card.hidden) visible += 1;
      });
      if (visible < 12) this._loadNextPage();
    }

    _applyTypeFilter() {
      // Each type tab is backed by its own collection, so the grid
      // contents are guaranteed by the server to match the active
      // type — no client-side type filtering needed. This stub
      // exists so legacy call sites (post load-more, post-favourites
      // restore, etc.) still get the pagination count refreshed
      // against whatever the incomplete filter left visible.
      if (!this.$grid) return;
      this._updatePaginationCount();
    }

    _refreshTabState() {
      if (!this.$grid) return;
      if (this.state.tab === 'favourites') {
        // Clone cached <li> DOM nodes for each favourite id. Cloning
        // (instead of rebuilding markup from string concat) means we
        // can't accidentally re-interpret a product title containing
        // markup characters as HTML — XSS-safe by construction.
        var favIds = Array.from(this.state.favourites);
        var nodes = favIds
          .map((id) => this._cardCache[id])
          .filter(Boolean)
          .map((node) => node.cloneNode(true));
        this.$grid.replaceChildren.apply(this.$grid, nodes);
        this.$grid.hidden = nodes.length === 0;
        if (this.$empty) this.$empty.hidden = nodes.length !== 0;
        this._wireGridCards();
        this._reflectHeartsState();
      } else {
        // "All" tab: restore the snapshot of the server-rendered grid
        // (or whatever the most recent filtered view was) so cards
        // come back when switching from Favourites → All. Source HTML
        // came from Liquid (server-escaped), so this innerHTML write
        // is safe.
        if (this._allGridHTML != null && this.$grid.innerHTML !== this._allGridHTML) {
          this.$grid.innerHTML = this._allGridHTML;
          this._wireGridCards();
          this._reflectHeartsState();
        }
        this.$grid.hidden = false;
        if (this.$empty) this.$empty.hidden = true;
      }
      this._applyTypeFilter();
    }

    _toggleFilters(force) {
      if (!this.$filterForm) return;
      var isOpen = this.$filterForm.classList.contains('is-open');
      var willOpen = typeof force === 'boolean' ? force : !isOpen;
      var form = this.$filterForm;
      if (willOpen) {
        form.classList.remove('is-closing');
        // Make sure the panel is in the DOM flow before we trigger
        // the transition (it ships with the `hidden` attribute so
        // it doesn't flash on first paint before our CSS loads).
        form.removeAttribute('hidden');
        // Force a reflow so the transition runs from the off-screen
        // start position (translateX(-100%)) to translateX(0).
        // eslint-disable-next-line no-unused-expressions
        form.offsetWidth;
        form.classList.add('is-open');
        this.$filtersBtn.setAttribute('aria-expanded', 'true');
      } else {
        form.classList.remove('is-open');
        form.classList.add('is-closing');
        this.$filtersBtn.setAttribute('aria-expanded', 'false');
        var onEnd = function (e) {
          if (e.target !== form || e.propertyName !== 'transform') return;
          form.classList.remove('is-closing');
          form.setAttribute('hidden', '');
          form.removeEventListener('transitionend', onEnd);
        };
        form.addEventListener('transitionend', onEnd);
      }
    }

    /* ───── render ───── */

    _formatPrice(cents) {
      if (this.formatter) return this.formatter.format(cents / 100);
      return (this.currencySymbol || '€') + (cents / 100).toFixed(2);
    }

    _wireGridCards() {
      if (!this.$grid) return;
      // (Re-)wire heart and add-to-wall handlers on the currently
      // rendered <li> cards. Replaces any old listeners by cloning
      // the buttons before binding.
      this.$grid.querySelectorAll('[data-awd-heart]').forEach((btn) => {
        var fresh = btn.cloneNode(true);
        btn.parentNode.replaceChild(fresh, btn);
        fresh.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._toggleFavourite(fresh.dataset.awdHeart);
        });
      });
      this.$grid.querySelectorAll('[data-awd-add]').forEach((btn) => {
        var fresh = btn.cloneNode(true);
        btn.parentNode.replaceChild(fresh, btn);
        fresh.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._addPoster(fresh.dataset.awdAdd);
        });
      });
    }

    /* ───── pagination (Show More button) ───── */

    _initPagination() {
      // Disconnect any previously-bound click listener (filters reload swap, etc).
      if (this._paginationBtn && this._onLoadMoreClick) {
        try { this._paginationBtn.removeEventListener('click', this._onLoadMoreClick); } catch (e) { /* noop */ }
      }
      this._paginationBtn = null;
      this._paginationLoading = false;
      if (!this.$grid) return;
      var sentinel = this.$grid.querySelector(':scope > [data-awd-pagination]');
      this.$pagination = sentinel || null;
      if (!sentinel) return;

      var btn = sentinel.querySelector('[data-awd-load-more]');
      if (!btn) return;
      var self = this;
      this._onLoadMoreClick = function (e) {
        e.preventDefault();
        self._loadNextPage();
      };
      btn.addEventListener('click', this._onLoadMoreClick);
      this._paginationBtn = btn;
    }

    _loadNextPage() {
      // Guards.
      if (this._paginationLoading) return;
      if (this.state.tab !== 'all') return;
      var sentinel = this.$pagination;
      if (!sentinel) return;
      var current = parseInt(sentinel.dataset.currentPage || '1', 10);
      var total = parseInt(sentinel.dataset.totalPages || '1', 10);
      if (!current || !total || current >= total) {
        this._removePaginationSentinel();
        return;
      }

      var nextPage = current + 1;
      var sectionId = this.dataset.productsSectionId;
      var handle = this.state.collectionHandle || this.dataset.collectionHandle;
      if (!sectionId || !handle) return;
      var qs = this.state.filterParams || '';
      var url =
        '/collections/' + handle +
        '?section_id=' + encodeURIComponent(sectionId) +
        (qs ? '&' + qs : '') +
        '&page=' + nextPage;

      this._paginationLoading = true;
      sentinel.classList.add('is-loading');
      var self = this;
      fetch(url, { credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newGrid = doc.querySelector('[data-awd-product-grid]');
          // The new grid's children are the new cards followed
          // (optionally) by the next sentinel. Pull them out
          // separately so we never accidentally insert the sentinel
          // mid-list.
          var newCards = [];
          var newSentinel = null;
          if (newGrid) {
            Array.from(newGrid.children).forEach(function (child) {
              if (child.matches && child.matches('[data-awd-pagination]')) {
                newSentinel = child;
              } else {
                newCards.push(child);
              }
            });
          }

          if (newCards.length && self.$grid) {
            // Insert each new card BEFORE the (current) sentinel so
            // the sentinel always stays the last child of the grid.
            newCards.forEach(function (card) {
              self.$grid.insertBefore(card, sentinel);
              var p = self._productFromCard(card);
              if (p) {
                self._cardCache[p.id] = card.cloneNode(true);
                self._productCache[p.id] = p;
                self.products.push(p);
              }
            });
            // Refresh "All" snapshot so favourites → all restoration
            // includes the newly appended cards.
            self._allGridHTML = self.$grid.innerHTML;
            // Wire add/heart on appended cards.
            self._wireGridCards();
            self._reflectHeartsState();
            // If any of the newly appended cards happen to be a
            // pre-existing favourite, snapshot them now so the
            // Favourites tab survives a future reload.
            self._backfillFavouriteSnapshots();
          }

          // Replace the sentinel with the one from the response
          // (advances current_page / total_pages). If the response
          // has no sentinel, we've reached the last page — remove
          // ours and stop observing.
          if (newSentinel) {
            sentinel.replaceWith(newSentinel);
            self.$pagination = newSentinel;
            self._initPagination();
          } else {
            self._removePaginationSentinel();
          }

          // Re-apply both filters to the freshly inserted cards.
          // Must run AFTER the sentinel replacement above so any
          // recursive _loadNextPage triggered by _maybeAutoLoadMore
          // uses the new sentinel (otherwise it would capture the
          // detached old one and fail on insertBefore).
          // _initPagination already cleared _paginationLoading so the
          // cascade can recurse. The final .then below also clears it
          // again, which is harmless.
          // - incomplete filter: hides cards with no parseable size
          //   (and may trigger an auto-load).
          // - type filter: respects the currently active type tab so
          //   the cascade keeps fetching pages until the visible
          //   count is healthy or we hit the last page.
          // Run even when the page appended ZERO cards — the server
          // skips sold-out products, so a page can legitimately arrive
          // empty, and _applyIncompleteFilter → _maybeAutoLoadMore is
          // what keeps the top-up cascade going to the next page.
          self._applyIncompleteFilter();
          self._applyTypeFilter();
        })
        .catch(function (err) {
          // eslint-disable-next-line no-console
          console.error('[awd] failed to load next page', err);
          sentinel.classList.remove('is-loading');
        })
        .then(function () {
          self._paginationLoading = false;
        });
    }

    _removePaginationSentinel() {
      if (this._paginationBtn && this._onLoadMoreClick) {
        try { this._paginationBtn.removeEventListener('click', this._onLoadMoreClick); } catch (e) { /* noop */ }
      }
      this._paginationBtn = null;
      if (this.$pagination && this.$pagination.parentNode) {
        this.$pagination.parentNode.removeChild(this.$pagination);
      }
      this.$pagination = null;
    }

    _reflectHeartsState() {
      if (!this.$grid) return;
      this.$grid.querySelectorAll('[data-awd-heart]').forEach((btn) => {
        var pressed = this.state.favourites.has(btn.dataset.awdHeart);
        btn.setAttribute('aria-pressed', pressed ? 'true' : 'false');
        btn.setAttribute(
          'aria-label',
          pressed ? 'Remove from favourites' : 'Add to favourites'
        );
      });
    }

    _renderFilterCount() {
      if (!this.$filterCount) return;
      // Count how many filter inputs are currently checked / set
      // (excluding sort_by which always has a value).
      var n = 0;
      if (this.$filterForm) {
        this.$filterForm.querySelectorAll('input[type="checkbox"]:checked').forEach(() => { n += 1; });
        // Price range counts as 1 filter when either the min or max input has a
        // value (i.e. the user narrowed the range away from the defaults).
        this.$filterForm.querySelectorAll('[data-range-holder]').forEach((holder) => {
          var lo = holder.querySelector('[data-field-price-min]');
          var hi = holder.querySelector('[data-field-price-max]');
          if ((lo && lo.value) || (hi && hi.value)) n += 1;
        });
      }
      this.$filterCount.textContent = '(' + n + ')';
    }

    _renderFavCount() {
      this.$favCount.textContent = '(' + this.state.favourites.size + ')';
    }

    /* ───── filter form (Section Rendering API) ───── */

    _wireFilterForm() {
      if (!this.$filterForm) return;
      var self = this;
      var debounceTimer = null;
      var debounce = function (fn, wait) {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(fn, wait);
      };

      // Header close / footer apply (rebind after every form swap
      // since these buttons live inside the swapped form).
      var $close = this.$filterForm.querySelector('[data-awd-filter-form-close]');
      if ($close) $close.addEventListener('click', function () { self._toggleFilters(false); });
      var $apply = this.$filterForm.querySelector('[data-awd-filter-form-apply]');
      if ($apply) $apply.addEventListener('click', function () { self._toggleFilters(false); });

      // Submit / change handlers — single delegated listener so we
      // don't have to re-bind on every DOM swap (form is replaced
      // wholesale).
      var onChange = function (e) {
        var t = e.target;
        if (!t || !t.name) return;
        debounce(function () { self._submitFilterForm(); }, 0);
      };
      this.$filterForm.addEventListener('change', onChange);
      this.$filterForm.addEventListener('submit', function (e) {
        e.preventDefault();
        self._submitFilterForm();
      });

      // Show more / show less — reveal or re-hide filter values past the cap.
      // Uses the collection filters' markup (collection-filters-form.js): the
      // [data-show-more]/[data-show-less] links live in .collection__sidebar__actions,
      // the over-cap items carry [data-link-hidden], and `hidden` toggles them.
      // Delegated so it survives the wholesale form swap on each filter fetch.
      this.$filterForm.addEventListener('click', function (e) {
        var moreBtn = e.target.closest('[data-show-more]');
        var lessBtn = e.target.closest('[data-show-less]');
        if (!moreBtn && !lessBtn) return;
        e.preventDefault();
        var actions = (moreBtn || lessBtn).closest('.collection__sidebar__actions');
        if (!actions) return;
        var list = actions.previousElementSibling; // the <ul> of options
        var hiddenItems = list ? list.querySelectorAll('[data-link-hidden]') : [];
        var showMore = actions.querySelector('[data-show-more]');
        var showLess = actions.querySelector('[data-show-less]');
        var reveal = !!moreBtn;
        hiddenItems.forEach(function (li) { li.classList.toggle('hidden', !reveal); });
        if (showMore) showMore.classList.toggle('hidden', reveal);
        if (showLess) showLess.classList.toggle('hidden', !reveal);
      });

      // Price range — the collection's <range-slider> fires `theme:range:update`
      // on commit. Sync its values into the price number inputs (clearing them at
      // the range extremes so no redundant price param is sent), update the
      // display, then submit. Mirrors collection-filters-form.js's updateRange.
      this.$filterForm.addEventListener('theme:range:update', function (e) {
        var rs = e.target && e.target.closest ? e.target.closest('range-slider') : null;
        if (!rs && e.target && e.target.matches && e.target.matches('range-slider')) rs = e.target;
        if (!rs) return;
        var holder = rs.closest('[data-range-holder]');
        if (!holder) return;
        var seMin = rs.getAttribute('data-se-min');
        var seMax = rs.getAttribute('data-se-max');
        var loVal = rs.getAttribute('data-se-min-value');
        var hiVal = rs.getAttribute('data-se-max-value');
        var minInput = holder.querySelector('[data-field-price-min]');
        var maxInput = holder.querySelector('[data-field-price-max]');
        if (minInput) {
          minInput.value = (loVal === seMin) ? '' : loVal;
          minInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
        if (maxInput) {
          maxInput.value = (hiVal === seMax) ? '' : hiVal;
          maxInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
        debounce(function () { self._submitFilterForm(); }, 0);
      });

      var resetBtn = this.$filterForm.querySelector('[data-awd-filter-reset]');
      if (resetBtn) {
        resetBtn.addEventListener('click', function (e) {
          e.preventDefault();
          self.$filterForm.reset();
          // Manually clear all checkboxes + price fields (reset() may not on
          // dynamic forms). Submitting then re-renders the form fresh, which
          // also resets the range-slider.
          self.$filterForm.querySelectorAll('input[type="checkbox"]').forEach((cb) => { cb.checked = false; });
          self.$filterForm.querySelectorAll('[data-field-price-min], [data-field-price-max]').forEach((i) => { i.value = ''; });
          self._submitFilterForm();
        });
      }

      this._renderFilterCount();
    }

    _submitFilterForm() {
      if (!this.$filterForm) return;
      var fd = new FormData(this.$filterForm);
      // Drop empty entries so the URL stays clean.
      var params = new URLSearchParams();
      fd.forEach(function (value, key) {
        if (value !== '' && value != null) params.append(key, value);
      });
      var qs = params.toString();
      this.state.filterParams = qs;
      this._fetchProducts(qs);
    }

    _fetchProducts(qs) {
      var sectionId = this.dataset.productsSectionId;
      var handle = this.state.collectionHandle || this.dataset.collectionHandle;
      if (!sectionId || !handle) return;
      var self = this;
      var url = '/collections/' + handle + '?section_id=' + encodeURIComponent(sectionId) + (qs ? '&' + qs : '');
      // Cancel any in-flight fetch so a stale response can't clobber
      // the latest one (user toggling filters quickly).
      if (this._fetchAbort) {
        try { this._fetchAbort.abort(); } catch (e) { /* noop */ }
      }
      var ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
      this._fetchAbort = ctrl;
      // Visual hint: dim the grid while loading.
      if (this.$grid) this.$grid.setAttribute('aria-busy', 'true');
      fetch(url, { credentials: 'same-origin', signal: ctrl ? ctrl.signal : undefined })
        .then((r) => {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.text();
        })
        .then((html) => {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newRoot = doc.querySelector('[data-awd-products-root]');
          if (!newRoot || !self.$productsRoot) return;
          // Preserve filter form open state across swap.
          var wasOpen = self.$filterForm && self.$filterForm.classList.contains('is-open');
          self.$productsRoot.replaceWith(newRoot);
          self.$productsRoot = newRoot;
          self.$grid = newRoot.querySelector('[data-awd-product-grid]');
          self.$filterForm = newRoot.querySelector('[data-awd-filter-form]');
          self.$empty = newRoot.querySelector('[data-awd-empty]');
          if (self.$filterForm && wasOpen) {
            self.$filterForm.removeAttribute('hidden');
            self.$filterForm.classList.add('is-open');
          }
          self._hydrateProductsFromDom();
          self._wireGridCards();
          self._wireFilterForm();
          self._initPagination();
          self._refreshTabState();
          // Hide cards whose variants have no parseable physical
          // size (so they can't be hung on the wall). Run after
          // _refreshTabState so the snapshot it caches reflects the
          // filtered grid. (Wall objects without a "Product Size"
          // option never reach the client — awd-product-card.liquid
          // skips them server-side.)
          self._applyIncompleteFilter();
          // Re-render the wall + subtotal: wall items hydrated from
          // localStorage may not have had their product available
          // until this fetch completed.
          self._renderWall();
          self._updateSubtotal();
          // Update URL bar without reloading.
          try {
            var pageUrl = new URL(window.location.href);
            pageUrl.search = qs ? ('?' + qs) : '';
            window.history.replaceState({}, '', pageUrl.toString());
          } catch (e) { /* noop */ }
        })
        .catch((err) => {
          if (err && err.name === 'AbortError') return;
          // eslint-disable-next-line no-console
          console.error('[awd] failed to fetch filtered products', err);
        })
        .finally(() => {
          if (self._fetchAbort === ctrl) self._fetchAbort = null;
          if (self.$grid) self.$grid.removeAttribute('aria-busy');
        });
    }

    _toggleFavourite(id) {
      id = String(id);
      var nowFav = !this.state.favourites.has(id);
      if (nowFav) this.state.favourites.add(id);
      else this.state.favourites.delete(id);
      writeJSON(LS_FAVS, Array.from(this.state.favourites));
      // Mirror the change into the shopper's Swish wishlist so the
      // heart here and the wishlist page stay one and the same list.
      this._syncFavouriteToWishlist(id, nowFav);
      // Persist a snapshot of the card markup so Favourites can
      // still display the product after a reload even when its
      // collection page hasn't been loaded yet (e.g. user paged
      // forwards via Load More, favourited an item on page 3,
      // then reloaded — page 3 isn't fetched on the next visit).
      this._writeFavouriteSnapshot(id);
      this._renderFavCount();
      // Update heart in place + (re-render only the favourites tab list)
      var btn = this.$grid.querySelector('[data-awd-heart="' + id + '"]');
      if (btn) {
        var pressed = this.state.favourites.has(id);
        btn.setAttribute('aria-pressed', pressed ? 'true' : 'false');
        btn.setAttribute(
          'aria-label',
          pressed ? 'Remove from favourites' : 'Add to favourites'
        );
      }
      if (this.state.tab === 'favourites') this._refreshTabState();
    }

    // Persist (or remove) a snapshot of the `<li>` card for a single
    // favourited product into localStorage. Used so the Favourites
    // tab can re-render those products after a reload even when the
    // collection page they live on hasn't been fetched yet.
    _writeFavouriteSnapshot(id) {
      try {
        var snaps = readJSON(LS_FAV_CARDS, {}) || {};
        if (this.state.favourites.has(id)) {
          var card = (this.$grid && this.$grid.querySelector('[data-awd-product-id="' + id + '"]'))
            || this._cardCache[id];
          if (card) snaps[id] = card.outerHTML;
        } else if (snaps[id]) {
          delete snaps[id];
        }
        // Drop any snapshots whose id is no longer favourited so the
        // payload stays bounded over time.
        Object.keys(snaps).forEach((k) => {
          if (!this.state.favourites.has(k)) delete snaps[k];
        });
        writeJSON(LS_FAV_CARDS, snaps);
      } catch (e) { /* localStorage quota / parse errors — non-fatal */ }
    }

    // Hydrate the in-memory _cardCache and _productCache from the
    // localStorage snapshots written by _writeFavouriteSnapshot.
    _hydrateFavouritesFromStorage() {
      var snaps = readJSON(LS_FAV_CARDS, {});
      if (!snaps || typeof snaps !== 'object') return;
      var doc = document.implementation.createHTMLDocument('');
      var self = this;
      Object.keys(snaps).forEach(function (id) {
        if (!self.state.favourites.has(id)) return;
        if (self._cardCache[id]) return;
        try {
          doc.body.innerHTML = snaps[id];
          var node = doc.body.firstElementChild;
          if (!node || node.getAttribute('data-awd-product-id') !== id) return;
          self._cardCache[id] = node;
          // Reuse the canonical card→product parser (it also carries
          // productType / isWallObject, which the canvas-frame and
          // wall-object logic branch on). The __fromSnapshot flag marks
          // the entry as frozen at heart-time so fresh grid data
          // overrides it and availability checks re-verify it live.
          var favProduct = self._productFromCard(node);
          if (favProduct) {
            favProduct.__fromSnapshot = true;
            self._productCache[id] = favProduct;
          }
        } catch (e) { /* malformed snapshot — skip */ }
      });
    }

    // Make sure every currently-favourited product whose card IS in
    // the live DOM has a snapshot in localStorage. Lets users who
    // favourited items before this code existed still see them in
    // the Favourites tab after a reload — no need to re-heart.
    _backfillFavouriteSnapshots() {
      if (!this.$grid) return;
      var snaps;
      try { snaps = readJSON(LS_FAV_CARDS, {}) || {}; } catch (e) { snaps = {}; }
      var changed = false;
      this.state.favourites.forEach((id) => {
        if (snaps[id]) return;
        var card = this.$grid.querySelector('[data-awd-product-id="' + id + '"]');
        if (card) {
          snaps[id] = card.outerHTML;
          changed = true;
        }
      });
      if (changed) {
        try { writeJSON(LS_FAV_CARDS, snaps); } catch (e) { /* quota — non-fatal */ }
      }
    }

    /* ───── Swish (Wishlist King) bridge ─────
     *
     * The Favourites tab is the shopper's real wishlist, not a private
     * localStorage list. Once the Wishlist King SDK is on the page
     * (window.WishlistKing, loaded by the app embed) we:
     *   - subscribe to the "mine" wishlist and make its product ids the
     *     favourites set (localStorage becomes a warm-start cache only),
     *   - fetch an AWD card for any wishlisted product that isn't in a
     *     loaded grid (hearted on a PDP / collection page), via the
     *     awd-favourite-card fragment section,
     *   - push heart clicks to app.addWishlistItem / removeWishlistItem.
     * Until the SDK is ready (or if it never loads — app disabled, ad
     * blocker) hearts keep working locally exactly as before, and any
     * clicks made in the meantime are replayed once it arrives.
     */
    _initWishlistBridge() {
      if (this._wkBooted) return;
      this._wkBooted = true;
      this._wkItems = Object.create(null); // productId -> { wishlistItemId, handle }
      this._wkPending = [];                // [{ id, fav }] clicks made before the SDK was ready
      this._wkInflight = Object.create(null); // productId -> true while an add/remove is in flight
      this._wkCardFetches = Object.create(null); // productId -> Promise
      var self = this;
      var tries = 0;
      var attach = function () {
        if (self._wkApp || !window.WishlistKing) return false;
        self._attachWishlistApp(window.WishlistKing);
        return true;
      };
      if (attach()) return;
      this._onWkLoad = function () { attach(); };
      document.addEventListener('wk:loader:wishlist-app:load', this._onWkLoad);
      // The loader event fires before the app is constructed in some
      // versions, so poll as well (cheap; stops after ~20s).
      this._wkPoll = setInterval(function () {
        tries += 1;
        if (attach() || tries > 80) {
          clearInterval(self._wkPoll);
          self._wkPoll = null;
        }
      }, 250);
    }

    _teardownWishlistBridge() {
      if (this._wkPoll) { clearInterval(this._wkPoll); this._wkPoll = null; }
      if (this._wkMigrateTimer) { clearTimeout(this._wkMigrateTimer); this._wkMigrateTimer = null; }
      if (this._onWkLoad) {
        document.removeEventListener('wk:loader:wishlist-app:load', this._onWkLoad);
        this._onWkLoad = null;
      }
      if (this._wkSubscription && this._wkSubscription.unsubscribe) {
        try { this._wkSubscription.unsubscribe(); } catch (e) { /* noop */ }
      }
      this._wkSubscription = null;
      this._wkApp = null;
      this._wkBooted = false;
    }

    _attachWishlistApp(app) {
      var self = this;
      if (!app || !app.state || typeof app.state.observeWishlist !== 'function') return;
      this._wkApp = app;
      if (this._wkPoll) { clearInterval(this._wkPoll); this._wkPoll = null; }
      try {
        this._wkSubscription = app.state
          .observeWishlist({ wishlistId: 'mine' })
          .subscribe(function (wishlist) { self._onWishlistState(wishlist); });
      } catch (e) {
        this._wkApp = null;
        return;
      }
      // Replay hearts clicked before the SDK was ready.
      var pending = this._wkPending.splice(0);
      pending.forEach(function (op) { self._syncFavouriteToWishlist(op.id, op.fav); });
      // Favourites hearted before this bridge existed live only in
      // localStorage. Push them into the wishlist once the first state
      // emission has told us what's already there — or after a grace
      // period if none comes (the SDK doesn't emit for an empty list).
      this._wkMigrateTimer = setTimeout(function () {
        self._wkMigrateTimer = null;
        if (self._wkGotState) return;
        // No emission = the wishlist is empty. First visit since the
        // bridge shipped: seed it with the local hearts. Any later visit:
        // the wishlist is the truth, so the local list must be empty too
        // (e.g. the shopper removed everything on the wishlist page).
        if (self._migrateLocalFavourites()) return;
        self._onWishlistState({ items: [] });
      }, 4000);
    }

    // One-off per browser: move favourites that only exist locally into
    // the wishlist. Returns true when a migration was started.
    _migrateLocalFavourites() {
      if (this._wkMigrated || !this._wkApp) return false;
      this._wkMigrated = true;
      var already = false;
      try { already = !!localStorage.getItem(LS_FAVS_MIGRATED); } catch (e) { /* noop */ }
      try { localStorage.setItem(LS_FAVS_MIGRATED, '1'); } catch (e) { /* noop */ }
      if (already) return false;
      var self = this;
      var started = false;
      this.state.favourites.forEach(function (id) {
        if (self._wkItems[id] || self._wkInflight[id]) return;
        started = true;
        self._syncFavouriteToWishlist(id, true);
      });
      return started;
    }

    // Wishlist state → favourites. Runs on every emission (initial load,
    // optimistic add/remove, revalidation), so it must be idempotent.
    _onWishlistState(wishlist) {
      var self = this;
      if (this._wkIdleResolve) { var r = this._wkIdleResolve; this._wkIdleResolve = null; r(); }
      this._wkGotState = true;
      var items = (wishlist && wishlist.items) || [];
      var next = new Set();
      var map = Object.create(null);
      items.forEach(function (item) {
        var product = item && item.product;
        if (!product || product.id == null) return;
        var id = String(product.id);
        next.add(id);
        map[id] = { wishlistItemId: item.id, handle: product.handle || '' };
      });
      this._wkItems = map;
      // Keep the local value for ids whose add/remove hasn't settled yet,
      // otherwise a stale emission would flip the heart back mid-flight.
      Object.keys(this._wkInflight).forEach(function (id) {
        if (self.state.favourites.has(id)) next.add(id);
        else next.delete(id);
      });
      var changed = next.size !== this.state.favourites.size;
      if (!changed) {
        next.forEach(function (id) { if (!self.state.favourites.has(id)) changed = true; });
      }
      this.state.favourites = next;
      writeJSON(LS_FAVS, Array.from(next));
      this._renderFavCount();
      this._reflectHeartsState();
      // Make sure every favourite has a card to show; products that were
      // hearted outside the designer need one fetched.
      var missing = [];
      next.forEach(function (id) {
        if (self._cardCache[id]) return;
        var handle = map[id] && map[id].handle;
        if (handle) missing.push(self._ensureFavouriteCard(id, handle));
      });
      this._backfillFavouriteSnapshots();
      var rerender = function () {
        if (self.state.tab === 'favourites') self._refreshTabState();
      };
      if (missing.length) Promise.all(missing).then(rerender);
      else if (changed) rerender();
      // First real emission: now we know what the wishlist already holds.
      if (!this._wkMigrated) this._migrateLocalFavourites();
    }

    // Resolves after the wishlist's next state emission (which is when the
    // SDK clears its "action pending" flag and accepts the next call), or
    // after a timeout so a missing emission can never wedge the queue.
    _wkWaitIdle(ms) {
      var self = this;
      return new Promise(function (resolve) {
        var done = false;
        var finish = function () {
          if (done) return;
          done = true;
          if (self._wkIdleResolve === finish) self._wkIdleResolve = null;
          resolve();
        };
        self._wkIdleResolve = finish;
        setTimeout(finish, ms || 1500);
      });
    }

    // Fetch the AWD card markup for a wishlisted product that isn't in
    // any loaded grid, and register it in the card / product caches.
    _ensureFavouriteCard(id, handle) {
      var self = this;
      if (this._cardCache[id]) return Promise.resolve(true);
      if (this._wkCardFetches[id]) return this._wkCardFetches[id];
      var root = (window.theme && window.theme.routes && window.theme.routes.root) || '/';
      if (root.charAt(root.length - 1) !== '/') root += '/';
      var url = root + 'products/' + encodeURIComponent(handle) + '?section_id=awd-favourite-card';
      this._wkCardFetches[id] = fetch(url, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) {
          if (!html) return false;
          var doc = document.implementation.createHTMLDocument('');
          doc.body.innerHTML = html;
          var card = doc.querySelector('[data-awd-product-id="' + id + '"]');
          if (!card) return false; // not an AWD product (frame, passepartout…)
          self._cardCache[id] = card.cloneNode(true);
          var p = self._productFromCard(card);
          if (p && !self._productCache[id]) self._productCache[id] = p;
          self._writeFavouriteSnapshot(id);
          return true;
        })
        .catch(function () { return false; })
        .then(function (ok) {
          delete self._wkCardFetches[id];
          return ok;
        });
      return this._wkCardFetches[id];
    }

    // Heart click → wishlist app. `fav` is the state the shopper just
    // chose; the local favourites set was already updated optimistically.
    _syncFavouriteToWishlist(id, fav) {
      var self = this;
      var app = this._wkApp;
      if (!app) {
        // Collapse repeated clicks on the same product to the last one.
        this._wkPending = (this._wkPending || []).filter(function (op) { return op.id !== id; });
        this._wkPending.push({ id: id, fav: fav });
        return;
      }
      this._wkInflight[id] = true;
      // The SDK silently ignores add/remove calls made while a previous
      // one is still settling, so every call goes through one queue and
      // waits for the wishlist to report back before the next runs.
      this._wkQueue = (this._wkQueue || Promise.resolve()).then(function () {
        // Decide against the wishlist's CURRENT state — an earlier queued
        // op or another tab may have changed it since the click.
        var known = self._wkItems[id];
        var wantFav = self.state.favourites.has(id);
        var call;
        var handle = '';
        if (wantFav) {
          if (known) return;
          var p = self._productById(id);
          var card = self.$grid && self.$grid.querySelector('[data-awd-product-id="' + id + '"]');
          handle = (p && p.handle) || (card && card.dataset.awdHandle) || '';
          if (!handle) return;
          call = app.addWishlistItem({ productHandle: handle });
        } else {
          if (!known) return;
          call = app.removeWishlistItem({ wishlistItemId: known.wishlistItemId });
        }
        var idle = self._wkWaitIdle(2500);
        return Promise.resolve(call)
          .then(function (res) {
            if (res === undefined) return; // SDK was busy; the state emission will tell the truth
            var item = res && res.wishlistItem;
            if (wantFav && item) self._wkItems[id] = { wishlistItemId: item.id, handle: handle };
            else if (!wantFav) delete self._wkItems[id];
          }, function () {
            // Server said no: roll the heart back to what the wishlist holds.
            if (known) self.state.favourites.add(id);
            else self.state.favourites.delete(id);
          })
          .then(function () { return idle; });
      }).then(function () {
        delete self._wkInflight[id];
        writeJSON(LS_FAVS, Array.from(self.state.favourites));
        self._renderFavCount();
        self._reflectHeartsState();
        if (self.state.tab === 'favourites') self._refreshTabState();
      });
    }

    _updateSubtotal() {
      var self = this;
      var total = this.state.wall.reduce((sum, item) => {
        var p = self._productById(item.id);
        if (!p) return sum;
        var v = self._variantForItem(item, p);
        var price = (v && v.price != null) ? v.price : (p.price || 0);
        var passe = self._addonForItem('passepartout', item);
        var frame = self._addonForItem('frame', item);
        if (passe && passe.price != null) price += Number(passe.price) || 0;
        if (frame && frame.price != null) price += Number(frame.price) || 0;
        return sum + price;
      }, 0);
      this.$subtotal.textContent = this._formatPrice(total);
      this.$cta.disabled = this.state.wall.length === 0;
      this._renderDrawer(total);
    }

    /* ───── shop-this-art-wall drawer ───── */

    _toggleDrawer(force) {
      if (!this.$drawer) return;
      var willOpen =
        typeof force === 'boolean'
          ? force
          : !this.$drawer.classList.contains('is-open');
      if (willOpen && this.state.wall.length === 0) return;
      if (willOpen) {
        this.$drawer.removeAttribute('hidden');
        // Force reflow so the transform transition runs.
        // eslint-disable-next-line no-unused-expressions
        this.$drawer.offsetHeight;
        this.$drawer.classList.add('is-open');
        this.$drawer.setAttribute('aria-hidden', 'false');
        if (this.$drawerToggle) this.$drawerToggle.setAttribute('aria-expanded', 'true');
      } else {
        this.$drawer.classList.remove('is-open');
        this.$drawer.setAttribute('aria-hidden', 'true');
        if (this.$drawerToggle) this.$drawerToggle.setAttribute('aria-expanded', 'false');
        // After the slide-out transition finishes, hide the node so
        // it's removed from the a11y tree and tab order.
        var node = this.$drawer;
        setTimeout(function () {
          if (!node.classList.contains('is-open')) node.setAttribute('hidden', '');
        }, 260);
      }
    }

    _frameTypeLabel(item) {
      var f = item.frame || {};
      if (f.option_id === 'none') return '';
      return f.product_title ? String(f.product_title) : '';
    }

    _frameColorLabel(item) {
      var f = item.frame || {};
      return f.color_label ? String(f.color_label) : '';
    }

    _passpartoutColorLabel(item) {
      var p = item.passpartout || {};
      return p.color_label ? String(p.color_label) : '';
    }

    _passpartoutOptionLabel(item) {
      var p = item.passpartout || {};
      if (!p.option_id || p.option_id === 'none') return '';
      var addon = this._addonForItem('passepartout', item);
      return addon && addon.size ? String(addon.size) : '';
    }

    _frameOptionLabel(item) {
      var f = item.frame || {};
      if (!f.option_id || f.option_id === 'none') return '';
      var addon = this._addonForItem('frame', item);
      return addon && addon.size ? String(addon.size) : '';
    }

    _makeRowCell(text, className) {
      var span = document.createElement('span');
      if (className) span.className = className;
      span.textContent = text == null ? '' : text;
      return span;
    }

    _makeSubRow(label, value, sizeText, typeText, priceText) {
      // label = e.g. "+ PASSEPARTOUT", value = e.g. "WHITE"
      var row = document.createElement('div');
      row.className = 'awd__drawer__subrow';

      var labelCell = document.createElement('span');
      labelCell.className = 'awd__drawer__sub-label awd__drawer__cell--text';
      var labelTop = document.createElement('span');
      labelTop.textContent = label;
      var labelBottom = document.createElement('span');
      labelBottom.className = 'awd__drawer__sub-label-value';
      labelBottom.textContent = value;
      labelCell.appendChild(labelTop);
      if (value) labelCell.appendChild(labelBottom);

      row.appendChild(labelCell);
      row.appendChild(this._makeRowCell(sizeText, 'awd__drawer__cell--text'));
      row.appendChild(this._makeRowCell(typeText, 'awd__drawer__cell--text'));
      row.appendChild(this._makeRowCell(priceText, 'awd__drawer__cell--price'));
      return row;
    }

    _renderDrawer(total) {
      if (!this.$drawer || !this.$drawerRows) return;

      var self = this;
      var groups = [];

      this.state.wall.forEach(function (item) {
        var p = self._productById(item.id);
        if (!p) return;
        var variant = self._variantForItem(item, p) || {};
        var sizeLbl = variant.title || '';
        var posterPrice = (variant.price != null ? variant.price : (p.price || 0));

        var group = document.createElement('div');
        group.className = 'awd__drawer__group';

        // Main poster row.
        var row = document.createElement('div');
        row.className = 'awd__drawer__row';

        var productCell = document.createElement('span');
        productCell.className = 'awd__drawer__cell--product';
        var titleEl = document.createElement('p');
        titleEl.className = 'awd__drawer__product-title';
        titleEl.textContent = p.title || '';
        productCell.appendChild(titleEl);
        if (p.artist) {
          var artistEl = document.createElement('p');
          artistEl.className = 'awd__drawer__product-artist';
          artistEl.textContent = p.artist;
          productCell.appendChild(artistEl);
        }

        row.appendChild(productCell);
        row.appendChild(self._makeRowCell(sizeLbl, 'awd__drawer__cell--text'));
        row.appendChild(self._makeRowCell(self._productTypeLabel(p), 'awd__drawer__cell--text'));
        row.appendChild(self._makeRowCell(self._formatPrice(posterPrice), 'awd__drawer__cell--price'));
        group.appendChild(row);

        // Optional passpartout sub-row.
        var passOptLbl = self._passpartoutOptionLabel(item);
        if (passOptLbl) {
          var passColor = self._passpartoutColorLabel(item).toUpperCase();
          var passeAddon = self._addonForItem('passepartout', item);
          var passePrice = passeAddon && passeAddon.price != null
            ? self._formatPrice(Number(passeAddon.price) || 0)
            : '\u2014';
          group.appendChild(
            self._makeSubRow('+ PASSEPARTOUT', passColor, passOptLbl, 'Passpartout', passePrice)
          );
        }

        // Optional frame sub-row.
        var frameOptLbl = self._frameOptionLabel(item);
        var frameTypeLbl = self._frameTypeLabel(item);
        if (frameOptLbl && frameTypeLbl) {
          var frameColor = self._frameColorLabel(item).toUpperCase();
          var label = frameColor ? (frameTypeLbl + ' ' + frameColor).trim().toUpperCase() : frameTypeLbl.toUpperCase();
          var frameAddon = self._addonForItem('frame', item);
          var framePrice = frameAddon && frameAddon.price != null
            ? self._formatPrice(Number(frameAddon.price) || 0)
            : '\u2014';
          group.appendChild(
            self._makeSubRow('+ FRAME', label, frameOptLbl, 'Frame', framePrice)
          );
        }

        groups.push(group);
      });

      this.$drawerRows.replaceChildren.apply(this.$drawerRows, groups);

      var hasItems = groups.length > 0;
      if (this.$drawerEmpty) this.$drawerEmpty.hidden = hasItems;
      // Bag count mirrors the cart: count every constituent of each bundle, not
      // just the poster. A poster with a passepartout and/or frame becomes a
      // multi-item bundle at checkout (its `_bundleItems` carry the print +
      // each add-on), so the bag should read poster + passepartout + frame.
      // Skip items whose product no longer resolves (they aren't rendered above).
      var bagCount = this.state.wall.reduce(function (sum, item) {
        if (!self._productById(item.id)) return sum;
        var n = 1; // the poster line itself
        if (self._passpartoutOptionLabel(item)) n += 1;
        if (self._frameOptionLabel(item) && self._frameTypeLabel(item)) n += 1;
        return sum + n;
      }, 0);
      if (this.$drawerCount) this.$drawerCount.textContent = '(' + bagCount + ')';
      if (this.$drawerSubtotal) {
        this.$drawerSubtotal.textContent = this._formatPrice(total || 0);
      }
      if (this.$drawerAdd) this.$drawerAdd.disabled = !hasItems;
      if (this.$drawerCheckout) this.$drawerCheckout.disabled = !hasItems;

      // Auto-close drawer if the wall was emptied while it was open.
      if (!hasItems && this.$drawer.classList.contains('is-open')) {
        this._toggleDrawer(false);
      }
    }

    _buildBundleItems() {
      // Emit cart lines for the wall.
      //
      // With a passepartout and/or frame: a MULTI-LINE bundle — the print and
      // each add-on are SEPARATE cart lines sharing `_bundleId` / `_role` (via
      // `window.theme.buildBundleItems`). The cart-transformer Function
      // `merge`s each group into one bundle line at checkout, parent = the
      // print variant, priced as the sum of the real lines' native costs (no
      // baked prices). Mirrors the PDP print-configurator.
      //
      // Without any addon: a plain line (print variant only, no `_bundle*`
      // attributes).
      var self = this;
      var items = [];
      var SOURCE = 'art-wall-designer';
      (this.state.wall || []).forEach(function (it) {
        var product = self._productById(it.id);
        if (!product) return;
        var variant = self._variantForItem(it, product);
        var printVid = variant ? variant.id : it.variant_id;
        if (!printVid) return;

        var hasAddon = Boolean(it.passepartout_variant_id || it.frame_variant_id);

        // The poster's artist, carried into the cart / checkout / order
        // confirmation as a visible "Artist" line item property — same
        // as the PDP add-to-cart. `product.artist` is sourced from the
        // card's data-awd-artist attribute.
        var artistName = product.artist ? String(product.artist).trim() : '';

        // Plain line (no addons) — same path PDP uses when neither a
        // passepartout nor a frame is selected. Only `_source` is sent
        // so the order still carries Art Wall Designer attribution.
        if (!hasAddon) {
          var plainProps = { _source: SOURCE };
          if (artistName) plainProps.Artist = artistName;
          if (variant && variant.sku) plainProps.SKU = variant.sku;
          items.push({
            id: Number(printVid),
            quantity: 1,
            properties: plainProps,
          });
          return;
        }

        // Bundle price in cart-currency cents. Variant prices in the
        // products config are already in cart-currency cents.
        var printPriceCents = Number((variant && variant.price != null) ? variant.price : (product.price || 0)) || 0;

        // Collect addons in the normalized shape consumed by
        // `window.theme.buildBundleProperties` (assets/js/utils/
        // bundle-properties.js). Each entry: { variantId, priceCents,
        // awdKey, label, hex }. The shared util builds the
        // `_bundleItems` / `_bundleAmounts` / `_bundlePrices` arrays
        // and the `_awd*` properties from this list.
        var addons = [];

        if (it.passepartout_variant_id) {
          var passeAddon = self._addonForItem ? self._addonForItem('passepartout', it) : null;
          var passeAddonCents = (passeAddon && passeAddon.price != null) ? (Number(passeAddon.price) || 0) : 0;
          var passeColor =
            (it.passpartout && it.passpartout.color_label)
              ? String(it.passpartout.color_label)
              : (passeAddon && passeAddon.color ? String(passeAddon.color) : '');
          var passeSize = (passeAddon && passeAddon.size) ? String(passeAddon.size) : '';
          var passeSwatch =
            (it.passpartout && it.passpartout.color_hex)
              ? String(it.passpartout.color_hex)
              : (passeAddon && passeAddon.swatch_color ? String(passeAddon.swatch_color) : '');
          var passeLabel = passeColor || (passeAddon && passeAddon.product_title) || 'Passepartout';
          // `passeSize` already contains its own size formatting
          // (e.g. "A4 (21x29.7 cm)"), so append it as-is rather than
          // wrapping it in another set of parens.
          if (passeSize) passeLabel += ' ' + passeSize;
          addons.push({
            variantId: it.passepartout_variant_id,
            priceCents: passeAddonCents,
            awdKey: 'passe',
            label: passeLabel,
            hex: passeSwatch,
            sku: passeAddon && passeAddon.sku,
          });
        }
        if (it.frame_variant_id) {
          var frameAddon = self._addonForItem ? self._addonForItem('frame', it) : null;
          var frameAddonCents = (frameAddon && frameAddon.price != null) ? (Number(frameAddon.price) || 0) : 0;
          var frameColor =
            (it.frame && it.frame.color_label)
              ? String(it.frame.color_label)
              : (frameAddon && frameAddon.color ? String(frameAddon.color) : '');
          var frameType =
            (it.frame && it.frame.product_title)
              ? String(it.frame.product_title)
              : (frameAddon && frameAddon.product_title ? String(frameAddon.product_title) : '');
          var frameSize = (frameAddon && frameAddon.size) ? String(frameAddon.size) : '';
          var frameSwatch =
            (it.frame && it.frame.color_hex)
              ? String(it.frame.color_hex)
              : (frameAddon && frameAddon.swatch_color ? String(frameAddon.swatch_color) : '');
          var frameLabel = [frameType, frameColor].filter(Boolean).join(' ');
          if (!frameLabel) frameLabel = 'Frame';
          // `frameSize` already contains its own size formatting
          // (e.g. "A4 (21x29,7 cm)"), so append it as-is rather than
          // wrapping it in another set of parens.
          if (frameSize) frameLabel += ' ' + frameSize;
          addons.push({
            variantId: it.frame_variant_id,
            priceCents: frameAddonCents,
            awdKey: 'frame',
            label: frameLabel,
            hex: frameSwatch,
            sku: frameAddon && frameAddon.sku,
          });
        }

        // Emit the bundle as SEPARATE cart lines (print + each add-on) sharing
        // `_bundleId` / `_role` via the shared util. The cart-transformer
        // `merge`s the group at checkout into ONE bundle line (parent = the
        // print variant) priced as the sum of the real lines' native costs —
        // no baked prices. The print image + addon label/swatch are baked for
        // the cart card (see buildBundleItems). `printPriceCents` is no longer
        // used (pricing is native).
        var printImageUrl =
          (product && (product.print_image || product.image)) || '';
        var lines = window.theme.buildBundleItems({
          printVariantId: printVid,
          quantity: 1,
          addons: addons,
          source: SOURCE,
          artist: artistName,
          printSku: variant && variant.sku,
          printImageUrl: printImageUrl,
        });
        lines.forEach(function (line) {
          items.push(line);
        });
      });
      return items;
    }

    // Last net before the cart: list the titles of every wall line —
    // print variant, passe-partout or frame — that current in-memory
    // data says is unavailable. Grid-backed products are live; snapshot
    // products were refreshed by _validateWallAvailability. Returning
    // the full list lets the caller name EVERY problem product at once
    // instead of Shopify's 422 naming only the first.
    _soldOutLineTitles() {
      var self = this;
      var out = [];
      var push = function (t) { if (t && out.indexOf(t) === -1) out.push(t); };
      var addonById = function (kind, id) {
        var list = (self.addons && self.addons[kind]) || [];
        return list.find(function (a) { return String(a.id) === String(id); }) || null;
      };
      (this.state.wall || []).forEach(function (it) {
        var p = self._productById(it.id);
        if (!p) return;
        var v = self._variantForItem(it, p);
        if (v && v.available === false) push(p.title);
        if (it.passepartout_variant_id) {
          var pa = addonById('passepartout', it.passepartout_variant_id);
          if (pa && pa.available === false) push((pa.product_title || 'Passepartout') + ' (' + p.title + ')');
        }
        if (it.frame_variant_id) {
          var fa = addonById('frame', it.frame_variant_id);
          if (fa && fa.available === false) push((fa.product_title || 'Frame') + ' (' + p.title + ')');
        }
      });
      return out;
    }

    _addToBag() {
      var self = this;
      var soldOut = this._soldOutLineTitles();
      if (soldOut.length) {
        window.alert('Sold out: ' + soldOut.join(', ') + '.\nPlease remove these items or pick another size.');
        return;
      }
      var items = this._buildBundleItems();
      if (!items.length) return;
      var btn = this.$drawerAdd;
      // Mirror the PDP add-to-cart animation: toggle .is-loading
      // while the fetch is in flight, then briefly show .is-added
      // (the inline checkmark) before closing the drawer. Theme.css
      // handles the cross-fade between .btn__text / .btn__loader /
      // .btn__added so we only need to manage the classes here.
      if (btn) {
        btn.disabled = true;
        btn.classList.remove('is-added');
        btn.classList.add('is-loading');
      }
      var cartAddUrl = ((window.theme && window.theme.routes && window.theme.routes.cart_add_url) || '/cart/add') + '.js';
      fetch(cartAddUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items: items }),
      })
        .then(function (r) {
          return r.text().then(function (txt) {
            var data = null;
            if (txt) { try { data = JSON.parse(txt); } catch (e) { data = { description: txt.slice(0, 200) }; } }
            return { ok: r.ok, status: r.status, data: data };
          });
        })
        .then(function (res) {
          if (!res.ok || (res.data && res.data.status >= 400)) throw res.data || res;
          // Refresh cart drawer count + body, then open it via the
          // theme's standard cart-toggle event (matches the PDP
          // print-configurator behaviour).
          var cartItems = document.querySelector('cart-items');
          if (cartItems && typeof cartItems.getCart === 'function') cartItems.getCart();
          if (btn) {
            btn.classList.remove('is-loading');
            btn.classList.add('is-added');
          }
          // Hold the checkmark on screen briefly so the user sees
          // the success state, then close the SHOP ART WALL drawer
          // and hand off to the site cart. When cartType ===
          // 'drawer' the cart drawer (cart-drawer__shell) opens
          // automatically because cartItems.getCart() above fires
          // `theme:product:added` when it finishes, which the
          // <cart-drawer> custom element listens for — we just
          // need to step out of the way by closing our own drawer.
          // When cartType is 'page' there is no drawer to open, so
          // we redirect to the cart page instead.
          setTimeout(function () {
            self._toggleDrawer(false);
            var cartType = window.theme && window.theme.settings && window.theme.settings.cartType;
            if (cartType !== 'drawer') {
              window.location = (window.theme && window.theme.routes && window.theme.routes.cart_url) || '/cart';
            }
            if (btn) {
              btn.classList.remove('is-added');
              btn.disabled = false;
            }
          }, 700);
        })
        .catch(function (err) {
          // eslint-disable-next-line no-console
          console.error('[awd] add to cart failed', err, 'items:', items);
          if (btn) {
            btn.classList.remove('is-loading');
            btn.classList.remove('is-added');
            btn.disabled = false;
          }
          window.alert((err && (err.description || err.message)) || 'Could not add bundle to cart.');
        });
    }

    _proceedToCheckout() {
      var soldOutCk = this._soldOutLineTitles();
      if (soldOutCk.length) {
        window.alert('Sold out: ' + soldOutCk.join(', ') + '.\nPlease remove these items or pick another size.');
        return;
      }
      var items = this._buildBundleItems();
      if (!items.length) {
        window.location = (window.theme && window.theme.routes && window.theme.routes.cart_url) || '/cart';
        return;
      }
      var btn = this.$drawerCheckout;
      if (btn) { btn.disabled = true; }
      var cartAddUrl = ((window.theme && window.theme.routes && window.theme.routes.cart_add_url) || '/cart/add') + '.js';
      fetch(cartAddUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items: items }),
      })
        .then(function (r) {
          return r.text().then(function (txt) {
            var data = null;
            if (txt) { try { data = JSON.parse(txt); } catch (e) { data = { description: txt.slice(0, 200) }; } }
            return { ok: r.ok, status: r.status, data: data };
          });
        })
        .then(function (res) {
          if (!res.ok || (res.data && res.data.status >= 400)) throw res.data || res;
          window.location = '/checkout';
        })
        .catch(function (err) {
          // eslint-disable-next-line no-console
          console.error('[awd] proceed to checkout failed', err, 'items:', items);
          window.alert((err && (err.description || err.message)) || 'Could not start checkout.');
          if (btn) btn.disabled = false;
        });
    }

    /* ───── confirm modal (replaces window.confirm) ───── */

    _confirm(message) {
      var self = this;
      return new Promise(function (resolve) {
        if (!self.$confirm) {
          // Fallback if the modal markup isn't present for any reason.
          resolve(window.confirm(message));
          return;
        }
        if (self.$confirmTitle && message) self.$confirmTitle.textContent = message;
        self.$confirm.removeAttribute('hidden');

        var cleanup = function (result) {
          self.$confirm.setAttribute('hidden', '');
          if (self.$confirmOk) self.$confirmOk.removeEventListener('click', onOk);
          if (self.$confirmCancel) self.$confirmCancel.removeEventListener('click', onCancel);
          if (self.$confirmBackdrop) self.$confirmBackdrop.removeEventListener('click', onCancel);
          document.removeEventListener('keydown', onKey);
          resolve(result);
        };
        var onOk = function () { cleanup(true); };
        var onCancel = function () { cleanup(false); };
        var onKey = function (e) {
          if (e.key === 'Escape') onCancel();
          else if (e.key === 'Enter') onOk();
        };

        if (self.$confirmOk) self.$confirmOk.addEventListener('click', onOk);
        if (self.$confirmCancel) self.$confirmCancel.addEventListener('click', onCancel);
        if (self.$confirmBackdrop) self.$confirmBackdrop.addEventListener('click', onCancel);
        document.addEventListener('keydown', onKey);

        // Move focus to the primary action so Enter / Esc work without
        // needing a click first.
        if (self.$confirmOk) {
          try { self.$confirmOk.focus({ preventScroll: true }); } catch (e) { /* noop */ }
        }
      });
    }

    /* ───── wall (canvas) ───── */

    _defaultVariantId(product) {
      var vs = (product && product.variants) || [];
      if (!vs.length) return null;
      // Prefer common framing-friendly sizes (in this order) so the
      // wall starts with a sensible default instead of the smallest
      // variant. Lead with 50x50 (square prints/canvas) and 50x70 so a
      // newly added print/canvas starts at one of those when available.
      var preferred = ['50x50', '50x70', '30x40', '40x50'];
      var norm = function (s) {
        return String(s || '')
          .toLowerCase()
          .replace(/\s+/g, '')
          .replace(/cm/g, '')
          .replace(/×/g, 'x');
      };
      for (var i = 0; i < preferred.length; i++) {
        var want = preferred[i];
        for (var j = 0; j < vs.length; j++) {
          var v = vs[j];
          if (norm(v.size || v.title).indexOf(want) !== -1 && v.available !== false) {
            return v.id;
          }
        }
      }
      // No preferred size in stock: fall back to the first AVAILABLE
      // variant (partially sold-out products must never start on a
      // sold-out size), then to the first variant as a last resort.
      for (var k = 0; k < vs.length; k++) {
        if (vs[k].available !== false) return vs[k].id;
      }
      return vs[0].id;
    }

    _addPoster(productId) {
      var p = this._productById(productId);
      if (!p) return;
      // Snapshot-backed products (e.g. a favourite restored from
      // localStorage) carry availability frozen at snapshot time — the
      // product may have sold out since, and sold-out products no longer
      // render grid cards that could correct the data. Check live BEFORE
      // adding (memoized, so repeat adds are instant) rather than adding
      // and silently deleting a moment later. (No handle → nothing to
      // check against; add on the snapshot's word.)
      if (p.__fromSnapshot && p.handle) {
        var self = this;
        this._fetchProductJson(p.handle).then(function (live) {
          if (live && live.variants) {
            var liveAvail = {};
            live.variants.forEach(function (v) { liveAvail[String(v.id)] = !!v.available; });
            (p.variants || []).forEach(function (v) {
              var key = String(v.id);
              if (key in liveAvail) v.available = liveAvail[key];
            });
          }
          var sellable = (p.variants || []).some(function (v) { return v.available !== false; });
          if (live === null || ((p.variants || []).length && !sellable)) {
            // Gone or fully sold out: don't add — mark the card the
            // user clicked so the dead end is visible, not silent.
            self._markCardSoldOut(productId);
            return;
          }
          // Inconclusive fetches (undefined) fall through and add with
          // the snapshot data — the cart pre-check is the last net.
          self._addPosterNow(p);
        });
        return;
      }
      this._addPosterNow(p);
    }

    // Mark every rendered card for this product — and the cached
    // favourites clone, so future tab renders stay marked — as sold out.
    _markCardSoldOut(productId) {
      var apply = function (card) {
        if (!card) return;
        card.classList.add('awd__card--sold-out');
        var btn = card.querySelector('[data-awd-add]');
        if (btn) {
          btn.disabled = true;
          var label = btn.querySelector('.awd__card__add__label');
          if (label) label.textContent = 'SOLD OUT';
        }
      };
      if (this.$grid) {
        this.$grid.querySelectorAll('[data-awd-product-id="' + productId + '"]').forEach(apply);
      }
      apply(this._cardCache[productId]);
    }

    _addPosterNow(p) {
      var productId = p.id;
      this._uidCounter += 1;
      var uid = 'p' + Date.now().toString(36) + this._uidCounter;
      var maxZ = this.state.wall.reduce((m, it) => Math.max(m, it.z || 0), 0);
      // Drop near the centre with a small random offset so stacking
      // posters don't perfectly overlap.
      var x = 50 + (Math.random() - 0.5) * 18;
      var y = 45 + (Math.random() - 0.5) * 16;
      this.state.wall.push({
        uid: uid,
        id: productId,
        x: x,
        y: y,
        z: maxZ + 1,
        variant_id: this._defaultVariantId(p),
        passpartout: { option_id: 'none' },
        frame:       { option_id: 'none' },
        // Snapshot of the product so the wall can render after a reload
        // even if this product isn't in the current grid page. Re-resolved
        // from the authoritative cards on next load (see
        // _hydrateWallProductData) so it never freezes stale styling. The
        // isWallObject flag also lets a snapshot-only reload suppress the
        // passe-partout / frame sections for wall objects.
        product_snapshot: this._snapshotFromProduct(p),
      });
      // Every print/canvas drops onto the wall framed in Oak by default
      // (wall objects excluded). Art prints can switch to NO FRAME after;
      // canvas stays enforced by _ensureCanvasDefaultFrame.
      this._applyDefaultFrame(this.state.wall[this.state.wall.length - 1], p);
      writeJSON(LS_WALL, this.state.wall);
      this._renderWall();
      this._updateSubtotal();
    }

    _variantForItem(item, product) {
      var vs = (product && product.variants) || [];
      if (!vs.length) return null;
      var vid = item && (item.variant_id != null ? String(item.variant_id) : null);
      if (vid) {
        var hit = vs.find(function (v) { return String(v.id) === vid; });
        if (hit) return hit;
      }
      return vs[0];
    }

    // Returns the matt addon record for the poster's current passe-partout
    // selection — carrying the OUTER matt size (`size`), the print size it
    // fits (`size_fit`), colour and image — or null when none is selected.
    // Prefers the values persisted on the item so it works before the
    // configure panel has built the variant map, and after a reload.
    _passeAddonForItem(item) {
      var p = (item && item.passpartout) || {};
      if (p.option_id === 'none') return null;
      if (p.size) return p;
      var vid = item && item.passepartout_variant_id;
      if (vid && this._passeVarMap && this._passeVarMap[String(vid)]) {
        return this._passeVarMap[String(vid)];
      }
      return null;
    }

    // Resolve a CSS colour value to a concrete one usable inside a
    // generated SVG (a data-URI SVG can't see the page's CSS custom
    // properties). e.g. "var(--yellow)" -> "rgb(255, 255, 0)". The probe
    // carries the `swatches` class so swatches.css variables are in scope.
    // Results are cached per input.
    _resolveCssColor(value) {
      if (!value) return '#ffffff';
      if (value.indexOf('var(') === -1) return value;
      this._colorCache = this._colorCache || {};
      if (this._colorCache[value]) return this._colorCache[value];
      if (!this._colorProbe) {
        this._colorProbe = document.createElement('span');
        this._colorProbe.className = 'swatches';
        this._colorProbe.style.cssText = 'position:absolute;left:-9999px;width:0;height:0;visibility:hidden;';
        (this.$canvas || this).appendChild(this._colorProbe);
      }
      this._colorProbe.style.color = value;
      var resolved = getComputedStyle(this._colorProbe).color || '#ffffff';
      this._colorCache[value] = resolved;
      return resolved;
    }

    // The matt board colour for a poster's passe-partout. Prefers the
    // dedicated matt hex (MATT_COLORS, keyed by colour handle), and falls
    // back to the resolved picker-swatch colour for any colour not yet
    // mapped.
    _mattColor(item) {
      var p = (item && item.passpartout) || {};
      var css = p.color_hex || '';
      var handle = '';
      var m = /var\(\s*--([a-z0-9-]+)\s*\)/i.exec(css);
      if (m) {
        handle = m[1].toLowerCase();
      } else if (p.color_label) {
        handle = String(p.color_label).toLowerCase().trim().replace(/\s+/g, '-');
      }
      if (handle && MATT_COLORS[handle]) return MATT_COLORS[handle];
      return this._resolveCssColor(css || '#ffffff');
    }

    _frameColor(item) {
      var f = (item && item.frame) || {};
      if (f.option_id === 'none' || !f.color_hex) return null;
      return f.color_hex;
    }

    // Returns the URL of the frame variant's attached image (the
    // frame mockup) if one is available for the current frame
    // selection on this poster, otherwise null.
    _frameImage(item) {
      var f = (item && item.frame) || {};
      if (f.option_id === 'none') return null;
      if (f.image) return f.image;
      // Backfill from the variant map for items hydrated from
      // localStorage before this field was persisted.
      var vid = item && item.frame_variant_id;
      if (vid && this._frameVarMap && this._frameVarMap[String(vid)]) {
        var addon = this._frameVarMap[String(vid)];
        if (addon && addon.swatch_image) {
          f.image = addon.swatch_image;
          return addon.swatch_image;
        }
      }
      return null;
    }

    _renderWall() {
      var self = this;
      var byId = function (id) { return self._productById(id); };

      // Reconcile posters by uid instead of wiping the stage, so adding an
      // item, changing a frame/matt, dragging or reordering doesn't re-create
      // — and flash — every other poster. Only the poster whose CONTENT
      // signature changed is rebuilt (reusing its art <img> so the artwork
      // doesn't reload); see _posterSignature.
      if (!this._posterEls) this._posterEls = {};
      var live = {};
      // Real-world scale: the background photo is rendered with
      // `background-size: cover`, so its effective on-screen height
      // depends on the canvas aspect vs the image aspect. We want a
      // poster's size on screen to track the *image*, not the canvas
      // — that way an A4 print looks the same fraction of the room
      // regardless of viewport.
      // Tuned to match the production scale (theposterclub.com): an A4
      // print should occupy roughly the same fraction of the room as on
      // production, so the perceived size feels realistic.
      var WALL_HEIGHT_CM = 292;
      // The generated matt's window is shrunk by this much (cm, per side)
      // relative to the print so the matt board laps over the print's edge
      // — a small realistic rebate. Larger = the matt covers more of the
      // print edge.
      var MATT_OVERLAP_CM = 0.4;
      var canvasRect = this.$canvas.getBoundingClientRect();
      var canvasW = canvasRect.width;
      var canvasH = canvasRect.height;
      var imgAspect = this._bgAspect || (canvasW / Math.max(canvasH, 1));
      var canvasAspect = canvasW / Math.max(canvasH, 1);
      // With `cover`, the smaller scale factor wins (image is sized so
      // it fully covers; the other axis overflows and is cropped).
      var renderedImgH;
      if (canvasAspect > imgAspect) {
        // Canvas is wider than image → image scaled to fill width.
        renderedImgH = canvasW / imgAspect;
      } else {
        // Canvas is taller than image → image scaled to fill height.
        renderedImgH = canvasH;
      }
      var pxPerCm = renderedImgH > 0 ? (renderedImgH / WALL_HEIGHT_CM) : 3;
      var sorted = this.state.wall.slice().sort((a, b) => (a.z || 0) - (b.z || 0));
      sorted.forEach((item) => {
        var p = byId(item.id);
        if (!p) return;
        live[item.uid] = true;
        var sig = this._posterSignature(item, p, pxPerCm);
        var existing = this._posterEls[item.uid];
        if (existing && existing.__awdSig === sig) {
          // Content unchanged — sync only the cheap positional / selection
          // state (no rebuild, so no flash).
          existing.style.left = item.x + '%';
          existing.style.top = item.y + '%';
          existing.style.zIndex = String(item.z || 1);
          existing.classList.toggle('is-selected', item.uid === this.state.selectedUid);
          return;
        }
        var reuseImg = existing
          ? existing.querySelector('.awd__poster__pad > img')
          : null;
        var variant = this._variantForItem(item, p) || { width_cm: 21, height_cm: 29.7 };
        // Variant cm dimensions are stored portrait-first (e.g. 30x40).
        // If the motif itself is landscape, swap so the wall preview
        // box mirrors the actual print orientation.
        var vW = variant.width_cm;
        var vH = variant.height_cm;
        if ((p.orientation || 'portrait') === 'landscape' && vW < vH) {
          var _tmp = vW; vW = vH; vH = _tmp;
        }
        var artW = vW * pxPerCm;
        var artH = vH * pxPerCm;

        // Passe-partout (matt) is added AROUND the print: the poster's
        // total size grows by the matt border on each side and the print
        // sits in the matt's window at TRUE scale (it is only inset, not
        // scaled down). The border is derived from the matt variant's
        // real OUTER size vs the print size it fits, so the matt PNG's
        // window lines up with the print edge — otherwise the matt over-
        // laps and crops the print. Border can differ on the X/Y axes
        // (e.g. a 50x70 print in a 70x100 matt). Falls back to a flat
        // estimate when the matt's outer size can't be parsed.
        var passOpt = (item.passpartout && item.passpartout.option_id) || 'none';
        var passPadX = 0;
        var passPadY = 0;
        if (passOpt !== 'none') {
          var matAddon = this._passeAddonForItem(item);
          var outer = matAddon && this._parseVariantSize(matAddon.size);
          if (outer) {
            // Outer dims are stored portrait-first; match the print's
            // orientation so the border maths line up for landscape.
            var outW = outer.width_cm;
            var outH = outer.height_cm;
            if ((p.orientation || 'portrait') === 'landscape' && outW < outH) {
              var _ot = outW; outW = outH; outH = _ot;
            }
            passPadX = Math.max(0, (outW - vW) / 2) * pxPerCm;
            passPadY = Math.max(0, (outH - vH) / 2) * pxPerCm;
          } else {
            var estCm = (passOpt === '30x40' ? 7 : 5);
            passPadX = estCm * pxPerCm;
            passPadY = estCm * pxPerCm;
          }
        }

        // Frame is rendered as a separate overlay <img> layer
        // (matching the homepage AWD module), not as a background
        // padding around the poster. The frame mockup's transparent
        // center reveals the artwork behind it; the visible frame
        // thickness is whatever the mockup PNG was drawn with.
        var frameColor = this._frameColor(item);
        var frameImage = this._frameImage(item);

        var totalW = artW + passPadX * 2;
        var totalH = artH + passPadY * 2;

        // Production uses a 3-layer structure (matching the existing
        // theposterclub.com site): outer poster wrapper with fixed
        // width/height; inside it a relative `__layer` container of
        // the same fixed size; inside that the print pad (also
        // fixed) plus the frame overlay as an absolutely-positioned
        // sibling filling 100%/100%. Each layer is sized in px from
        // the variant's cm dimensions × pxPerCm so the on-screen
        // physical scale is preserved across sizes.
        var el = document.createElement('div');
        // Canvas products get a modifier so CSS can soften their drop-shadow
        // (a canvas sits flusher to the wall than a framed print, so the
        // default shadow reads too hard).
        var isCanvasItem = /canvas/i.test((p && p.productType) || '');
        el.className =
          'awd__poster swatches' +
          (item.uid === this.state.selectedUid ? ' is-selected' : '') +
          (isCanvasItem ? ' awd__poster--canvas' : '');
        el.dataset.awdPosterUid = item.uid;
        el.style.width = totalW + 'px';
        el.style.height = totalH + 'px';
        el.style.left = item.x + '%';
        el.style.top = item.y + '%';
        el.style.zIndex = String(item.z || 1);

        // Layer 2: relative container that establishes the
        // positioning context for the absolute frame overlay.
        var layer = document.createElement('div');
        layer.className = 'awd__poster__layer';
        layer.style.position = 'relative';
        layer.style.width = totalW + 'px';
        layer.style.height = totalH + 'px';

        // Layer 3a: pad (fixed size) holding the print image, inset by
        // the matt thickness so the print sits in the matt's window. The
        // matt itself is a generated SVG overlay (below) stacked above the
        // print, so it can cast a soft inner shadow onto the print through
        // its window opening.
        var pad = document.createElement('div');
        pad.className = 'awd__poster__pad';
        if (passOpt !== 'none') {
          // White paper mount behind the matt window — a safety backing so
          // any sub-pixel reveal around the print reads as paper, never the
          // wall behind it. The visible matt board is the SVG overlay.
          pad.style.background = '#ffffff';
        }
        // Match production: a flat 2px padding so the pad reads as
        // the print's paper edge inside the frame. When a
        // passe-partout is selected, the matt board's padding
        // takes over and the 2px is dropped. Padding can differ per
        // axis (matt borders aren't always square).
        var basePadX = passOpt === 'none' ? 2 : passPadX;
        var basePadY = passOpt === 'none' ? 2 : passPadY;
        pad.style.padding = basePadY + 'px ' + basePadX + 'px';
        pad.style.width = totalW + 'px';
        pad.style.height = totalH + 'px';
        pad.style.boxSizing = 'border-box';

        var neededSrc = p.print_image || p.image;
        var img;
        if (reuseImg && reuseImg.getAttribute('src') === neededSrc) {
          // Reuse the already-loaded artwork node so a frame / matt change
          // doesn't reload (and flash) the print itself.
          img = reuseImg;
          if (img.parentNode) img.parentNode.removeChild(img);
        } else {
          img = document.createElement('img');
          img.src = neededSrc;
        }
        img.alt = p.title || '';
        img.draggable = false;
        img.style.width = '100%';
        img.style.height = '100%';
        img.style.display = 'block';
        img.style.transform = '';
        img.style.transformOrigin = '';
        img.style.objectFit = '';
        // Per-size print scale (see PRINT_SIZE_SCALE): for sizes whose box
        // aspect is squarer than the shared print aspect, the CSS `contain`
        // default letterboxes the print and leaves a gap on the sides under the
        // matt / frame. Scaling the print up a hair fills the box width; the
        // extra top/bottom is clipped by the pad's `overflow: hidden` below.
        // Defaults to 1 (no change) for any size not listed.
        var _printScale = 1;
        if (variant && variant.width_cm && variant.height_cm) {
          var _pfW = Math.round(variant.width_cm);
          var _pfH = Math.round(variant.height_cm);
          _printScale = PRINT_SIZE_SCALE[Math.min(_pfW, _pfH) + 'x' + Math.max(_pfW, _pfH)] || 1;
        }
        // With a passe-partout or a frame, nudge the print down a hair so its
        // edge tucks cleanly under the matt/frame rather than meeting it flush.
        var _tuckScale = (passOpt !== 'none' || frameImage || frameColor) ? 0.985 : 1;
        var _imgScale = _tuckScale * _printScale;
        if (_imgScale !== 1) {
          img.style.transform = 'scale(' + _imgScale + ')';
          img.style.transformOrigin = 'center center';
        }
        // Clip the print to the pad whenever it's scaled up past the box, so
        // the overflow doesn't spill onto the wall (the matt / frame overlays
        // already cover the border region). Reset to '' otherwise.
        pad.style.overflow = _printScale > 1 ? 'hidden' : '';
        pad.appendChild(img);
        layer.appendChild(pad);

        // Passe-partout (matt board) overlay — a GENERATED SVG sibling of
        // the pad, stacked ABOVE the print and BELOW the frame. It's a
        // solid-colour board (from the matt colour) with a transparent
        // window cut to the print, lapped slightly over the print's edge.
        // Because it's the correct size for the orientation already, no
        // rotation is needed. The CSS `drop-shadow` on `.awd__poster__pass`
        // casts the matt's shadow into the window onto the print behind it.
        if (passOpt !== 'none') {
          var mattOverlap = MATT_OVERLAP_CM * pxPerCm;
          var passWrap = document.createElement('div');
          passWrap.className = 'awd__poster__pass-wrap';
          passWrap.setAttribute('aria-hidden', 'true');
          var passImg = document.createElement('img');
          passImg.className = 'awd__poster__pass';
          passImg.src = svgMatt(this._mattColor(item), totalW, totalH, passPadX, passPadY, mattOverlap);
          passImg.alt = '';
          passImg.draggable = false;
          passWrap.appendChild(passImg);
          layer.appendChild(passWrap);
        }

        // Frame overlay layer — sibling of the pad inside the
        // relative `layer` container, absolutely positioned to fill
        // the poster bounding box. Matches production's structure
        // (and the homepage module).
        if (frameImage) {
          var frameWrap = document.createElement('div');
          frameWrap.className = 'awd__poster__frame-wrap';
          frameWrap.setAttribute('aria-hidden', 'true');
          var frameImg = document.createElement('img');
          frameImg.className = 'awd__poster__frame';
          frameImg.src = frameImage;
          frameImg.alt = '';
          frameImg.draggable = false;
          // Optional per-size fit correction (see FRAME_SIZE_TRANSFORM): some
          // frame mockups don't reach 100% of the print box at certain sizes,
          // so we apply a transform. Keyed by normalized "WxH" (smaller dim
          // first) so one entry covers both orientations.
          var frameFitTransform = '';
          if (variant && variant.width_cm && variant.height_cm) {
            var _fsW = Math.round(variant.width_cm);
            var _fsH = Math.round(variant.height_cm);
            var _frameSizeKey =
              Math.min(_fsW, _fsH) + 'x' + Math.max(_fsW, _fsH);
            frameFitTransform = FRAME_SIZE_TRANSFORM[_frameSizeKey] || '';
          }
          // Frame mockup PNGs are authored portrait. For landscape
          // prints we rotate the mockup 90° so the wood borders stay
          // uniform on all four sides (otherwise stretching a portrait
          // frame to landscape squashes the top/bottom rails). The
          // rotated img is sized to the SWAPPED container dims, then
          // centred + rotated so it ends up filling the landscape box.
          // The fit correction is composed in the mockup's own coordinate
          // space (before the 90° rotation) so it stretches the same
          // physical rails in both orientations.
          if ((p.orientation || 'portrait') === 'landscape') {
            frameImg.style.position = 'absolute';
            frameImg.style.top = '50%';
            frameImg.style.left = '50%';
            frameImg.style.width = totalH + 'px';
            frameImg.style.height = totalW + 'px';
            frameImg.style.transformOrigin = 'center center';
            frameImg.style.transform =
              'translate(-50%, -50%) rotate(90deg)' +
              (frameFitTransform ? ' ' + frameFitTransform : '');
          } else if (frameFitTransform) {
            frameImg.style.transformOrigin = 'center center';
            frameImg.style.transform = frameFitTransform;
          }
          frameWrap.appendChild(frameImg);
          layer.appendChild(frameWrap);
        } else if (frameColor) {
          // Fallback for items in state without a frame image (e.g.
          // legacy localStorage entries): render a coloured border
          // as a sibling overlay so the layering matches the image
          // path.
          var frameBorder = document.createElement('div');
          frameBorder.className = 'awd__poster__frame-wrap awd__poster__frame-wrap--color';
          frameBorder.setAttribute('aria-hidden', 'true');
          frameBorder.style.border = (1.5 * pxPerCm) + 'px solid ' + frameColor;
          frameBorder.style.boxSizing = 'border-box';
          layer.appendChild(frameBorder);
        }

        el.appendChild(layer);

        // Edit + Delete control stack (Sprint 06).
        var controls = document.createElement('div');
        controls.className = 'awd__poster__controls';
        controls.innerHTML =
          '<button type="button" class="awd__poster__btn" data-awd-edit aria-label="Edit poster">' +
          '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
          '<path d="M4 4h9v2H6v12h12v-7h2v9H4V4zm12.5-1.5 4 4L12 15H8v-4l8.5-8.5zm1.4 1.4-1.1 1.1 1.7 1.7 1.1-1.1-1.7-1.7z" fill="currentColor"/>' +
          '</svg></button>' +
          '<button type="button" class="awd__poster__btn" data-awd-delete aria-label="Remove poster">' +
          '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
          '<path d="M9 3h6l1 2h4v2H4V5h4l1-2zm-3 6h12l-1 11a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L6 9zm4 2v9h2v-9h-2zm4 0v9h2v-9h-2z" fill="currentColor"/>' +
          '</svg></button>';
        el.appendChild(controls);

        el.__awdSig = sig;
        if (existing && existing.parentNode) {
          existing.parentNode.replaceChild(el, existing);
        } else {
          this.$canvasStage.appendChild(el);
        }
        this._posterEls[item.uid] = el;
        this._attachPosterDrag(el, item);
        this._attachPosterControls(el, item, p);
      });

      // Drop posters no longer on the wall.
      Object.keys(this._posterEls).forEach((uid) => {
        if (live[uid]) return;
        var stale = this._posterEls[uid];
        if (stale && stale.parentNode) stale.parentNode.removeChild(stale);
        delete this._posterEls[uid];
      });
    }

    // Content fingerprint for a poster: everything that affects its inner
    // DOM / size, but NOT position (x/y), stack order (z) or selection —
    // those are applied as cheap style/class updates every render so they
    // never force a rebuild. Unchanged signature ⇒ keep the existing DOM
    // (and its already-loaded images) untouched.
    _posterSignature(item, p, pxPerCm) {
      var v = this._variantForItem(item, p) || { width_cm: 21, height_cm: 29.7 };
      var passOpt = (item.passpartout && item.passpartout.option_id) || 'none';
      var passKey = '';
      if (passOpt !== 'none') {
        var mat = this._passeAddonForItem(item);
        passKey = (this._mattColor(item) || '') + '|' + ((mat && mat.size) || '');
      }
      return [
        item.id,
        (p.print_image || p.image || ''),
        p.orientation || 'portrait',
        /canvas/i.test((p && p.productType) || '') ? 'c' : '',
        v.width_cm, v.height_cm,
        passOpt, passKey,
        this._frameImage(item) || '',
        this._frameColor(item) || '',
        Math.round(pxPerCm * 1000)
      ].join('~');
    }

    _attachPosterControls(el, item, product) {
      var self = this;

      // NOTE: We intentionally do NOT mark the poster "selected" on
      // pointerdown anymore. Selection used to trigger a full
      // _renderWall() which destroyed the DOM mid-drag, so the user
      // had to click first, then drag again. Controls are now revealed
      // by :hover / :focus-within (see CSS), so no JS state needed.

      el.querySelector('[data-awd-delete]').addEventListener('click', (e) => {
        e.stopPropagation();
        self.state.wall = self.state.wall.filter((it) => it.uid !== item.uid);
        if (self.state.selectedUid === item.uid) self.state.selectedUid = null;
        writeJSON(LS_WALL, self.state.wall);
        self._renderWall();
        self._updateSubtotal();
        // Close configure panel if it was showing this item.
        if (self._configureUid === item.uid) self._closeConfigure();
      });

      el.querySelector('[data-awd-edit]').addEventListener('click', (e) => {
        e.stopPropagation();
        self.state.selectedUid = item.uid;
        self._openConfigure(item.uid);
      });
    }

    _attachPosterDrag(el, item) {
      var dragState = null;
      var self = this;

      el.addEventListener('pointerdown', (e) => {
        if (e.button !== 0) return;
        // Don't start a drag if the press began on a control (handle,
        // delete/layer button) — they have their own handlers.
        if (e.target.closest('[data-awd-edit], [data-awd-delete]')) {
          return;
        }
        var rect = self.$canvas.getBoundingClientRect();
        dragState = {
          pointerId: e.pointerId,
          startX: e.clientX,
          startY: e.clientY,
          origX: item.x,
          origY: item.y,
          canvasW: rect.width,
          canvasH: rect.height,
          moved: false,
        };
        // Bring to front while dragging.
        var maxZ = self.state.wall.reduce((m, it) => Math.max(m, it.z || 0), 0);
        item.z = maxZ + 1;
        el.style.zIndex = String(item.z);
        el.classList.add('is-dragging');
        document.body.classList.add('awd-dragging');
        // Clear any active text selection so the browser doesn't keep
        // highlighting things while we drag.
        try { window.getSelection().removeAllRanges(); } catch (err) { /* ignore */ }
        e.preventDefault();
        try { el.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
      });

      el.addEventListener('pointermove', (e) => {
        if (!dragState || e.pointerId !== dragState.pointerId) return;
        var dxPct = ((e.clientX - dragState.startX) / dragState.canvasW) * 100;
        var dyPct = ((e.clientY - dragState.startY) / dragState.canvasH) * 100;
        if (!dragState.moved) {
          // Treat small movements as a tap, not a drag.
          var dxPx = e.clientX - dragState.startX;
          var dyPx = e.clientY - dragState.startY;
          if ((dxPx * dxPx + dyPx * dyPx) < 25) return; // <5px movement
          dragState.moved = true;
        }
        // Clamp so the poster's bounding box stays fully inside the
        // canvas. The poster is centered on (x%, y%) via translate(-50%,
        // -50%), so the visible half-extent in % is (size/2) / canvas.
        var posterRect = el.getBoundingClientRect();
        var halfWPct = (posterRect.width / 2 / dragState.canvasW) * 100;
        var halfHPct = (posterRect.height / 2 / dragState.canvasH) * 100;
        var minX = halfWPct;
        var maxX = 100 - halfWPct;
        var minY = halfHPct;
        var maxY = 100 - halfHPct;
        item.x = Math.max(minX, Math.min(maxX, dragState.origX + dxPct));
        item.y = Math.max(minY, Math.min(maxY, dragState.origY + dyPct));
        el.style.left = item.x + '%';
        el.style.top = item.y + '%';
      });

      function endDrag(e) {
        if (!dragState || e.pointerId !== dragState.pointerId) return;
        try { el.releasePointerCapture(dragState.pointerId); } catch (err) { /* ignore */ }
        var wasTap = !dragState.moved;
        dragState = null;
        el.classList.remove('is-dragging');
        document.body.classList.remove('awd-dragging');
        writeJSON(LS_WALL, self.state.wall);
        if (wasTap && e.type === 'pointerup') {
          // Click-on-poster opens the configure panel for it.
          self.state.selectedUid = item.uid;
          self._openConfigure(item.uid);
        }
      }
      el.addEventListener('pointerup', endDrag);
      el.addEventListener('pointercancel', endDrag);
      // Block the native HTML5 drag (which causes browsers to render
      // a ghost image and to keep elements highlighted).
      el.addEventListener('dragstart', (e) => e.preventDefault());
    }

    /* ───── scenes / background ───── */

    _applyBackground() {
      var self = this;
      var scene = this.scenes.find((s) => s.id === this.state.backgroundId) || this.scenes[0];
      this.$canvasBg.style.backgroundImage = 'url("' + scene.url + '")';

      // Preload the image so we know its natural aspect ratio. The
      // wall-relative poster scale depends on the rendered height of
      // the background image (which `background-size: cover` decides
      // based on the canvas aspect vs the image aspect).
      var img = new Image();
      img.onload = function () {
        self._bgAspect = img.naturalWidth / img.naturalHeight;
        if (self.products && self.products.length) self._renderWall();
      };
      img.src = scene.url;
    }

    _renderBgModal() {
      if (!this.$bgModalCards) return;
      var self = this;
      // The modal stages a "draft" selection so the user can browse
      // without immediately applying. CONFIRM commits it; X / overlay
      // discards it.
      var pending = this._bgModalPendingId || this.state.backgroundId;
      this.$bgModalCards.replaceChildren.apply(
        this.$bgModalCards,
        this.scenes.map(function (s) {
          var card = document.createElement('button');
          card.type = 'button';
          card.className = 'awd__bg-card' + (s.id === pending ? ' is-active' : '');
          card.setAttribute('data-awd-bg', s.id);
          card.setAttribute('aria-pressed', s.id === pending ? 'true' : 'false');
          card.innerHTML =
            '<span class="awd__bg-card__media" style="background-image:url(' +
            JSON.stringify(s.url).replace(/"/g, "'") +
            ')"></span>' +
            '<span class="awd__bg-card__label"></span>';
          card.querySelector('.awd__bg-card__label').textContent = s.label;
          card.addEventListener('click', function () {
            self._bgModalPendingId = s.id;
            self._renderBgModal();
          });
          return card;
        })
      );
      // Defer until after layout so scrollWidth is correct.
      requestAnimationFrame(function () { self._updateBgModalProgress(); });
    }

    _updateBgModalProgress() {
      if (!this.$bgModalScroller || !this.$bgModalProgress) return;
      var el = this.$bgModalScroller;
      var overflow = el.scrollWidth - el.clientWidth;
      if (overflow <= 1) {
        this.$bgModalProgress.setAttribute('hidden', '');
        return;
      }
      this.$bgModalProgress.removeAttribute('hidden');
      var visibleRatio = el.clientWidth / el.scrollWidth;
      var scrollRatio = el.scrollLeft / overflow;
      var trackWidth = this.$bgModalProgress.clientWidth || 1;
      var barWidth = Math.max(24, trackWidth * visibleRatio);
      var maxLeft = trackWidth - barWidth;
      this.$bgModalProgressBar.style.width = barWidth + 'px';
      this.$bgModalProgressBar.style.transform = 'translateX(' + (scrollRatio * maxLeft) + 'px)';
    }

    _toggleBgModal(force) {
      if (!this.$bgModal) return;
      var willOpen =
        typeof force === 'boolean'
          ? force
          : this.$bgModal.hasAttribute('hidden');
      if (willOpen) {
        this._bgModalPendingId = this.state.backgroundId;
        this.$bgModal.removeAttribute('hidden');
        // requestAnimationFrame so the transition plays.
        var modal = this.$bgModal;
        requestAnimationFrame(function () { modal.classList.add('is-open'); });
        this.$bgToggle.setAttribute('aria-expanded', 'true');
        this._renderBgModal();
      } else {
        this.$bgModal.classList.remove('is-open');
        this.$bgToggle.setAttribute('aria-expanded', 'false');
        var node = this.$bgModal;
        setTimeout(function () {
          if (!node.classList.contains('is-open')) node.setAttribute('hidden', '');
        }, 220);
        this._bgModalPendingId = null;
      }
    }

    _confirmBgModal() {
      var pending = this._bgModalPendingId;
      if (pending && pending !== this.state.backgroundId) {
        this.state.backgroundId = pending;
        localStorage.setItem(LS_BG, this.state.backgroundId);
        this._applyBackground();
      }
      this._toggleBgModal(false);
    }

    /* ───── configure panel (Sprint 06) ───── */

    _openConfigure(uid) {
      this._configureUid = uid;
      // Canvas items are always framed; apply the default frame before
      // rendering so the wall preview and the panel stay in sync.
      var cfgItem = this.state.wall.find((w) => w.uid === uid);
      if (cfgItem && this._ensureCanvasDefaultFrame(cfgItem)) {
        writeJSON(LS_WALL, this.state.wall);
        this._renderWall();
      }
      this.$sidebar.hidden = true;
      this.$configure.hidden = false;
      this._renderConfigure();
    }

    _closeConfigure() {
      this._configureUid = null;
      this.$configure.hidden = true;
      this.$sidebar.hidden = false;
    }

    _renderConfigure() {
      var item = this.state.wall.find((w) => w.uid === this._configureUid);
      if (!item) { this._closeConfigure(); return; }
      var product = this._productById(item.id);
      if (!product) { this._closeConfigure(); return; }

      var html = '';
      html +=
        '<div class="awd__cfg__product">' +
        '<h2 class="awd__cfg__title">' + product.title + '</h2>' +
        '<p class="awd__cfg__artist">' + product.artist + '</p>' +
        '</div>';

      // Artwork Size — populated from the product's actual variants
      // (per-product, not a global size list). Sorted smallest → biggest
      // by area so chips read A4 → 30×40 → 50×70 → 70×100 etc.
      var productVariants = (product.variants || []).slice().sort(function (a, b) {
        var aArea = (a.width_cm || 0) * (a.height_cm || 0);
        var bArea = (b.width_cm || 0) * (b.height_cm || 0);
        return aArea - bArea;
      });
      var currentVariant = this._variantForItem(item, product);
      var currentVid = currentVariant ? String(currentVariant.id) : null;
      // Hide the entire Artwork Size section when the product has a
      // single "Default Title" variant — typical of wall objects
      // with no real size option. Showing a chip labelled "Default
      // Title" with a heading of "Artwork Size" looks like a bug.
      var isDefaultOnlyVariant =
        productVariants.length === 1 &&
        /^default title$/i.test((productVariants[0].title || '').trim());
      if (!isDefaultOnlyVariant) {
        html += '<section class="awd__cfg__section">';
        html += '<h3 class="awd__cfg__heading">Artwork Size</h3>';
        html += '<div class="awd__cfg__chips">';
        productVariants.forEach((v) => {
          var active = String(v.id) === currentVid;
          // Sold-out sizes render disabled (mirrors the PDP's sold-out
          // treatment) — a disabled button emits no click, so the
          // delegated [data-awd-cfg] handler never fires for them.
          var soldOut = v.available === false;
          html += '<button type="button" class="awd__cfg__chip' + (active ? ' is-active' : '') + (soldOut ? ' is-sold-out' : '') + '"' + (soldOut ? ' disabled aria-disabled="true"' : '') + ' data-awd-cfg="size" data-awd-value="' + v.id + '">' + v.title + '</button>';
        });
        html += '</div></section>';
      }

      // Passpartout + Frames are populated directly from the real
      // Shopify add-on variants emitted by the section liquid (drawn
      // from the shop-global `print_configuration` metaobject):
      //   1. Filter add-ons to those whose own size matches the print
      //      variant's currently selected size — same normalization
      //      the PDP <print-configurator> uses.
      //   2. If no add-ons fit this size, the entire section is hidden.
      //   3. Otherwise: render NONE/YES pills + a row of colour
      //      circles, one per fitting addon variant. Each circle
      //      carries its real variant id so add-to-cart can build a
      //      proper bundle.
      var sizeNorm = this._normalizeSize((currentVariant && (currentVariant.size || currentVariant.title)) || '');
      // Canvas products use a dedicated set of frames (product type
      // "Canvas Frames") and never offer a passepartout. Regular
      // prints get all OTHER frames (i.e. excluding canvas frames).
      var isCanvasProduct = /canvas/i.test(product.productType || '');
      // Wall objects are hung as-is — they never take a passe-partout or
      // a frame, so neither the option sections nor their "none for this
      // size" empty-state messages should ever appear for them.
      var isWallObject = !!product.isWallObject;
      // Limited Edition prints are sold as-is — no passepartout or frame
      // (mirrors the PDP, which sets show_addons = false for this type).
      // Treated like wall objects so neither add-on section appears.
      var isLimitedEdition = /limited\s*edition/i.test(product.productType || '');
      // Passepartouts: filter by the variant's `size_fit` metafield
      // (the inner print size the mat fits), NOT by the variant's
      // own size option (the outer mat). Mirrors the PDP
      // <print-configurator> logic exactly.
      var passeAvail = (isCanvasProduct || isLimitedEdition) ? [] : (this.addons && this.addons.passepartout || []).filter((a) => {
        return a.available && this._normalizeSize(a.size_fit) === sizeNorm;
      });
      // Frames fit the OUTER edge of whatever they surround. With a
      // passe-partout selected that's the matt's OUTER size (e.g. a
      // 50x70 print in a 60x80 matt needs a 60x80 frame); with no
      // passe-partout it's the print's own size. Keying the frame
      // filter — and the frame size label below — off this makes the
      // frame options follow the passe-partout selection.
      var passeOn = !!(item.passpartout && item.passpartout.option_id && item.passpartout.option_id !== 'none');
      var mattAddon = passeOn ? this._passeAddonForItem(item) : null;
      var frameSizeSource = (mattAddon && mattAddon.size) ? mattAddon.size : ((currentVariant && (currentVariant.size || currentVariant.title)) || '');
      // Fitting frames for the current (matt-aware) size, ordered Oak →
      // Dark Oak → rest. Centralised in _framesForItem so add / open /
      // size-change reuse the same, identically-sorted set.
      var frameAvail = this._framesForItem(item, product);

      // Map variant_id → addon record (used by _applyConfigChange).
      var passeVarMap = {};
      passeAvail.forEach((a) => { passeVarMap[String(a.id)] = a; });
      var frameVarMap = {};
      frameAvail.forEach((a) => { frameVarMap[String(a.id)] = a; });
      this._passeVarMap = passeVarMap;
      this._frameVarMap = frameVarMap;

      // Helper: render one swatch circle for an addon record. The
      // `swatches` class brings in the global colour-name CSS custom
      // properties (defined in `assets/swatches.css`) so a swatch_color
      // like `var(--white)` or `var(--light-oak)` resolves correctly.
      function addonSwatchHtml(a, isActive, kind) {
        var bg = '';
        // Frames AND passepartouts: prefer the solid colour (matches
        // the Figma colour-circle design). The variant image is reserved
        // for the wall preview / eligibility gating, not the swatch.
        if (kind === 'frame' || kind === 'passepartout') {
          if (a.swatch_color) {
            bg = 'background:' + a.swatch_color + ';';
          } else if (a.swatch_image) {
            bg = "background-image:url('" + a.swatch_image + "');background-size:cover;";
          }
        } else if (a.swatch_image) {
          bg = "background-image:url('" + a.swatch_image + "');background-size:cover;";
        } else if (a.swatch_color) {
          bg = 'background:' + a.swatch_color + ';';
        }
        var label = (a.color || a.product_title || '').replace(/"/g, '&quot;');
        return (
          '<button type="button" class="awd__cfg__swatch swatches' + (isActive ? ' is-active' : '') +
          '" style="' + bg + '" data-awd-cfg="' + (kind === 'passepartout' ? 'pass-color' : 'frame-color') +
          '" data-awd-value="' + a.id + '" aria-label="' + label + '" title="' + label + '"></button>'
        );
      }

      // Passpartout — only render if any real variant fits this size.
      // Pills mirror the PDP behaviour: a NO / YES pair where the YES
      // pill is implicitly the matching size (the poster's currently
      // selected size). Colour circles below come from the real Shopify
      // add-on variants filtered by that same size.
      if (passeAvail.length) {
        var passOptId = (item.passpartout && item.passpartout.option_id) || 'none';
        var isPasseOn = passOptId !== 'none';
        // Several passe-partouts can fit one print at DIFFERENT outer
        // sizes (e.g. an A4 print mats to both 30x40 and A3). Each distinct
        // outer size is its own selectable option (a size pill); the
        // colours live under the chosen size — not all flattened into one
        // list of "duplicate" colours.
        var passeSizeOrder = [];
        var passeSizeMap = {};
        passeAvail.forEach((a) => {
          var key = this._normalizeSize(a.size);
          if (!passeSizeMap[key]) {
            passeSizeMap[key] = { key: key, label: String(a.size || '').toUpperCase(), items: [] };
            passeSizeOrder.push(key);
          }
          passeSizeMap[key].items.push(a);
        });
        var selPasseSizeKey = this._normalizeSize((item.passpartout && item.passpartout.size) || '');
        // Default to the FIRST size's colours when nothing is selected yet,
        // so the customer can always see the colour options up front. Picking
        // a colour auto-selects that size (see the `pass-color` handler).
        if (!passeSizeMap[selPasseSizeKey]) selPasseSizeKey = passeSizeOrder[0];

        html += '<section class="awd__cfg__section">';
        html += '<h3 class="awd__cfg__heading">Passpartout</h3>';
        html += '<div class="awd__cfg__chips">';
        html += '<button type="button" class="awd__cfg__chip' + (!isPasseOn ? ' is-active' : '') + '" data-awd-cfg="pass-option" data-awd-value="none">NO PASSPARTOUT</button>';
        passeSizeOrder.forEach((key) => {
          var grp = passeSizeMap[key];
          var active = isPasseOn && key === selPasseSizeKey;
          html += '<button type="button" class="awd__cfg__chip' + (active ? ' is-active' : '') + '" data-awd-cfg="pass-size" data-awd-value="' + key + '">' + grp.label + '</button>';
        });
        html += '</div>';
        // Always show the colour swatches (even with NO PASSPARTOUT active)
        // so the options are visible up front. They come from the first size
        // until the customer makes a selection. A swatch only renders as
        // active once the passe-partout is actually on.
        var passColorGroup = passeSizeMap[selPasseSizeKey] || { items: [] };
        html += '<p class="awd__cfg__sub">COLOR</p>';
        html += '<div class="awd__cfg__swatches">';
        passColorGroup.items.forEach((a) => {
          var active = isPasseOn && String(a.id) === String(item.passepartout_variant_id);
          html += addonSwatchHtml(a, active, 'passepartout');
        });
        html += '</div>';
        html += '</section>';
      } else if (!isCanvasProduct && !isWallObject && !isLimitedEdition) {
        // No passe-partout fits this size (e.g. 70x100). Rather than
        // silently hiding the section, tell the customer so the absence
        // is clearly intentional. Canvas products and wall objects never
        // offer a passe-partout, so the message would be misleading there.
        html += '<section class="awd__cfg__section">';
        html += '<h3 class="awd__cfg__heading">Passpartout</h3>';
        html += '<p class="awd__cfg__sub">No passepartout for this size</p>';
        html += '</section>';
      }

      // Frames — only render if any real variant fits this size.
      // Pills mirror the PDP: NO / YES pair where YES = the poster's
      // currently selected size. Colour swatches come from real Shopify
      // add-on variants, grouped by product_title (e.g. "Oak Frame").
      if (frameAvail.length) {
        // Canvas prints are always sold framed (mirrors the PDP's
        // data-pc-frame-required): guarantee a frame is selected so the
        // NO FRAME chip can be omitted and a swatch shows as active.
        if (isCanvasProduct) this._ensureCanvasDefaultFrame(item, product);
        var frameOptId = (item.frame && item.frame.option_id) || 'none';
        var isFrameOn = frameOptId !== 'none';
        // Frame size follows the passe-partout (outer matt size) when one
        // is selected, otherwise the print size — see `frameSizeSource`.
        var frameSizeLabel = (frameSizeSource || '').toUpperCase();
        html += '<section class="awd__cfg__section">';
        html += '<h3 class="awd__cfg__heading">Frames</h3>';
        html += '<div class="awd__cfg__chips">';
        // Regular prints can be unframed; canvas omits the NO FRAME chip
        // and keeps the size chip permanently active.
        if (!isCanvasProduct) {
          html += '<button type="button" class="awd__cfg__chip' + (!isFrameOn ? ' is-active' : '') + '" data-awd-cfg="frame-option" data-awd-value="none">NO FRAME</button>';
        }
        var frameSizeChipActive = isCanvasProduct ? true : isFrameOn;
        html += '<button type="button" class="awd__cfg__chip' + (frameSizeChipActive ? ' is-active' : '') + '" data-awd-cfg="frame-option" data-awd-value="a3">' + frameSizeLabel + '</button>';
        html += '</div>';
        // Always show the frame colour swatches (even with NO FRAME active)
        // so the options are visible up front. They come from the current
        // frame size; picking a colour auto-selects that size (see the
        // `frame-color` handler). Swatches/headings only render as active
        // once a frame is actually selected (activeAddon is null when off).
        {
          // One combined list of all fitting frames in their natural
          // source order — no Oak / Colored / Aluminum split. The active
          // colour name is shown beside a single COLOR sub-heading
          // (mirrors the passe-partout section above).
          var activeAddon = null;
          frameAvail.forEach((a) => {
            if (String(a.id) === String(item.frame_variant_id)) activeAddon = a;
          });
          function activeColorLabel(a) {
            return String(a.product_title || a.color || '').replace(/\s*frames?$/i, '').toUpperCase();
          }
          var heading = 'COLOR';
          if (activeAddon) {
            heading += ' <span class="awd__cfg__sub__active">' + activeColorLabel(activeAddon) + '</span>';
          }
          html += '<p class="awd__cfg__sub">' + heading + '</p>';
          html += '<div class="awd__cfg__swatches">';
          frameAvail.forEach((a) => {
            var active = String(a.id) === String(item.frame_variant_id);
            html += addonSwatchHtml(a, active, 'frame');
          });
          html += '</div>';
        }
        html += '</section>';
      } else if (!isWallObject && !isLimitedEdition) {
        // No frame fits this size (e.g. 70x100). Mirror the passe-partout
        // empty-state so the absence is clearly intentional rather than
        // a silently hidden section. Wall objects never take a frame, so
        // the message would be misleading there.
        html += '<section class="awd__cfg__section">';
        html += '<h3 class="awd__cfg__heading">Frames</h3>';
        html += '<p class="awd__cfg__sub">No frames for this size</p>';
        html += '</section>';
      }

      this.$configureBody.innerHTML = html;
    }

    // Normalize a size label so "A4 (21×29.7 cm)", "a4", "A4" all
    // compare equal. Mirrors the same logic the PDP configurator uses.
    _normalizeSize(s) {
      if (!s) return '';
      return String(s).toLowerCase().replace(/×/g, 'x').replace(/,/g, '.').trim();
    }

    // Returns the chosen addon variant record (or null) for a wall item.
    _addonForItem(kind, item) {
      var vid = kind === 'passepartout' ? item.passepartout_variant_id : item.frame_variant_id;
      if (!vid || vid === 'none') return null;
      var list = (this.addons && this.addons[kind]) || [];
      for (var i = 0; i < list.length; i++) {
        if (String(list[i].id) === String(vid)) return list[i];
      }
      return null;
    }

    // Order frame swatches so Oak comes first, Dark Oak second, then the
    // rest in their natural source order. Stable (original index breaks
    // ties). Driving the row from this also makes the auto-picked default
    // frame Oak — see _ensureCanvasDefaultFrame and the `frame-option`
    // handler in _applyConfigChange.
    _sortFramesOakFirst(list) {
      function rank(a) {
        var t = ((a.product_title || '') + ' ' + (a.color || '')).toLowerCase();
        if (/dark\s*oak/.test(t)) return 1; // Dark Oak second
        if (/oak/.test(t)) return 0;        // Oak first
        return 2;                            // everything else, source order
      }
      return (list || [])
        .map(function (a, i) { return { a: a, i: i }; })
        .sort(function (x, y) { return rank(x.a) - rank(y.a) || x.i - y.i; })
        .map(function (o) { return o.a; });
    }

    // Fitting frame addon variants for an item at its current (matt-aware)
    // size — the same filter the configure panel uses — sorted Oak → Dark
    // Oak → rest. Centralised so add / open / size-change all pick from the
    // identical, identically-ordered set.
    _framesForItem(item, product) {
      product = product || this._productById(item.id);
      // Limited Edition prints are sold as-is — no frame (mirrors the PDP's
      // `show_addons = false` for that type). Return none so a frame is
      // never offered or auto-defaulted for them.
      if (/limited\s*edition/i.test((product && product.productType) || '')) return [];
      var variant = this._variantForItem(item, product);
      var isCanvasProduct = /canvas/i.test((product && product.productType) || '');
      var passeOn = !!(item.passpartout && item.passpartout.option_id && item.passpartout.option_id !== 'none');
      var mattAddon = passeOn ? this._passeAddonForItem(item) : null;
      var frameSizeSource = (mattAddon && mattAddon.size) ? mattAddon.size : ((variant && (variant.size || variant.title)) || '');
      var frameSizeNorm = this._normalizeSize(frameSizeSource);
      var list = (this.addons && this.addons.frame || []).filter((a) => {
        if (!a.available) return false;
        if (this._normalizeSize(a.size) !== frameSizeNorm) return false;
        var isCanvasFrame = /canvas\s*frames?/i.test(a.product_type || '');
        return isCanvasProduct ? isCanvasFrame : !isCanvasFrame;
      });
      return this._sortFramesOakFirst(list);
    }

    // Seed a wall item with the default frame (first fitting = Oak, after
    // the Oak-first sort) when it has none. Skips wall objects (never
    // framed). Used on ADD for every print/canvas so both drop onto the
    // wall framed in Oak by default. Returns true if it changed the item.
    // Art prints can still switch to NO FRAME afterwards (this only fills
    // an empty frame, it never overrides an existing choice).
    _applyDefaultFrame(item, product) {
      product = product || this._productById(item.id);
      if (!product || product.isWallObject) return false;
      var hasFrame = !!(item.frame && item.frame.option_id && item.frame.option_id !== 'none' && item.frame_variant_id);
      if (hasFrame) return false;
      var frames = this._framesForItem(item, product);
      if (!frames.length) return false;
      var f = frames[0];
      item.frame_variant_id = f.id;
      item.frame = {
        option_id: 'a3',
        type: f.product_title || 'frame',
        color_hex: f.swatch_color || '',
        color_label: f.color || '',
        image: f.swatch_image || '',
        product_title: f.product_title || '',
      };
      return true;
    }

    // Canvas prints are ALWAYS sold framed (mirrors the PDP's
    // data-pc-frame-required), so unlike art prints their frame is
    // re-asserted on open and after a size change — never left as NO FRAME.
    // No-op for non-canvas products.
    _ensureCanvasDefaultFrame(item, product) {
      product = product || this._productById(item.id);
      if (!product || !/canvas/i.test(product.productType || '')) return false;
      return this._applyDefaultFrame(item, product);
    }

    // Re-pick the frame variant so it matches the size the frame must
    // fit: the passe-partout's OUTER size when one is selected, otherwise
    // the print's own size. The frame's colour/material is preserved
    // (matched by `product_title`, which uniquely identifies it, e.g.
    // "Oak Frame" / "Dark Oak Frame" / "Yellow Frame"). If no equivalent
    // frame exists at the required size the frame is dropped rather than
    // left at a wrong size. No-op when no frame is selected.
    _resyncFrameToPasse(item, product) {
      var frame = item.frame || {};
      if (!frame.option_id || frame.option_id === 'none') return;
      product = product || this._productById(item.id);
      var passeOn = !!(item.passpartout && item.passpartout.option_id && item.passpartout.option_id !== 'none');
      var mattAddon = passeOn ? this._passeAddonForItem(item) : null;
      var variant = this._variantForItem(item, product);
      var frameSizeSource = (mattAddon && mattAddon.size) ? mattAddon.size : ((variant && (variant.size || variant.title)) || '');
      var frameSizeNorm = this._normalizeSize(frameSizeSource);
      if (!frameSizeNorm) return;
      var isCanvasProduct = /canvas/i.test((product && product.productType) || '');
      var prevTitle = String(frame.product_title || frame.type || '');
      var prevOption = frame.option_id;
      var self = this;
      var match = (this.addons.frame || []).find(function (a) {
        if (!a.available) return false;
        if (self._normalizeSize(a.size) !== frameSizeNorm) return false;
        var isCanvasFrame = /canvas\s*frames?/i.test(a.product_type || '');
        if (isCanvasProduct ? !isCanvasFrame : isCanvasFrame) return false;
        return String(a.product_title || '') === prevTitle;
      });
      if (match) {
        item.frame_variant_id = match.id;
        item.frame = {
          option_id: prevOption,
          type: match.product_title || 'frame',
          color_hex: match.swatch_color || '',
          color_label: match.color || '',
          image: match.swatch_image || '',
          product_title: match.product_title || '',
        };
      } else {
        item.frame = { option_id: 'none', type: 'none' };
        item.frame_variant_id = null;
      }
    }

    // Move a wall item onto a different print variant, preserving the
    // frame / passe-partout identity (colour/material, matched by
    // `product_title`) at the new size where an equivalent add-on
    // exists, and dropping an add-on only when nothing fits. Shared by
    // the configure panel's size chips, the availability validator and
    // the shared-wall hydration so every size change behaves the same.
    _setItemVariant(item, value) {
      // Capture the previous frame/passepartout identity so we can
      // try to preserve them across the size change (the variant
      // ids themselves are size-specific, but `product_title`
      // identifies the colour/material).
      var prevFrameTitle = item.frame && item.frame.product_title ? String(item.frame.product_title) : '';
      var prevFrameOption = item.frame && item.frame.option_id ? String(item.frame.option_id) : 'none';
      var prevPassTitle = item.passpartout && item.passpartout.product_title ? String(item.passpartout.product_title) : '';
      var prevPassOption = item.passpartout && item.passpartout.option_id ? String(item.passpartout.option_id) : 'none';

      item.variant_id = value;
      // Add-ons are size-bound; clear the variant ids and visual
      // metadata so we don't carry a pick that no longer fits.
      item.passepartout_variant_id = null;
      item.frame_variant_id = null;
      item.passpartout = { option_id: 'none' };
      item.frame = { option_id: 'none', type: 'none' };

      // Resolve the new print variant's size so we can search the
      // add-on lists for an equivalent variant at the new size.
      var product = this._productById(item.id);
      var newVariant = product && (product.variants || []).find(function (v) { return String(v.id) === String(value); });
      var newSizeNorm = this._normalizeSize((newVariant && (newVariant.size || newVariant.title)) || '');

      // Re-pick the passepartout first: same product (colour/material)
      // but for the new print size. Passepartouts are matched by their
      // `size_fit` (the print size they fit), NOT their outer size.
      if (prevPassOption !== 'none' && prevPassTitle && newSizeNorm) {
        var self2 = this;
        var matchPass = (this.addons.passepartout || []).find(function (a) {
          return a.available
            && self2._normalizeSize(a.size_fit) === newSizeNorm
            && String(a.product_title || '') === prevPassTitle;
        });
        if (matchPass) {
          item.passepartout_variant_id = matchPass.id;
          item.passpartout = {
            option_id: prevPassOption,
            color_hex: matchPass.swatch_color || '',
            color_label: matchPass.color || '',
            image: matchPass.swatch_image || '',
            size: matchPass.size || '',
            size_fit: matchPass.size_fit || '',
            product_title: matchPass.product_title || '',
          };
        }
      }

      // Re-pick the frame AFTER the passepartout, since the required
      // frame size depends on whether a passepartout is now selected
      // (frame fits the matt's outer size) or not (frame fits the
      // print). Reconstruct the previous frame identity so the resync
      // can match the same colour/material at the new size.
      if (prevFrameOption !== 'none' && prevFrameTitle) {
        item.frame = { option_id: prevFrameOption, type: prevFrameTitle, product_title: prevFrameTitle };
        this._resyncFrameToPasse(item, product);
      }
      // Canvas is always sold framed — re-assert the default frame if
      // no equivalent survived the size change. No-op for non-canvas.
      this._ensureCanvasDefaultFrame(item, product);
    }

    _applyConfigChange(kind, value) {
      var item = this.state.wall.find((w) => w.uid === this._configureUid);
      if (!item) return;
      if (kind === 'size') {
        // Never switch onto a sold-out variant (the chip is disabled,
        // but guard here too so no other code path can select one).
        var guardProduct = this._productById(item.id);
        var guardVariant = guardProduct && (guardProduct.variants || []).find(function (v) { return String(v.id) === String(value); });
        if (guardVariant && guardVariant.available === false) return;
        this._setItemVariant(item, value);
      } else if (kind === 'pass-option') {
        item.passpartout = item.passpartout || {};
        item.passpartout.option_id = value;
        if (value === 'none') {
          item.passepartout_variant_id = null;
        } else if (!item.passepartout_variant_id) {
          // First time selecting a non-none size: auto-pick the first
          // available real addon variant so the user immediately sees
          // a colour applied (matches how the PDP behaves).
          var first = (this._passeVarMap && Object.keys(this._passeVarMap)[0]) || null;
          if (first) {
            var firstAddon = this._passeVarMap[first];
            item.passepartout_variant_id = firstAddon.id;
            item.passpartout.color_hex = firstAddon.swatch_color || '';
            item.passpartout.color_label = firstAddon.color || '';
            item.passpartout.image = firstAddon.swatch_image || '';
            item.passpartout.size = firstAddon.size || '';
            item.passpartout.size_fit = firstAddon.size_fit || '';
            item.passpartout.product_title = firstAddon.product_title || '';
          }
        }
        // Toggling the passe-partout on/off changes the size a frame must
        // fit (matt outer vs. print), so re-pick any selected frame.
        this._resyncFrameToPasse(item, this._productById(item.id));
      } else if (kind === 'pass-size') {
        // Pick a passe-partout of the chosen OUTER size that fits this
        // print. Keep the current colour when that colour exists in the
        // new size, otherwise fall back to the first available colour.
        var pzProduct = this._productById(item.id);
        var pzVariant = this._variantForItem(item, pzProduct);
        var pzPrintNorm = this._normalizeSize((pzVariant && (pzVariant.size || pzVariant.title)) || '');
        var pzSizeKey = String(value);
        var pzInSize = (this.addons.passepartout || []).filter((a) => {
          return a.available
            && this._normalizeSize(a.size_fit) === pzPrintNorm
            && this._normalizeSize(a.size) === pzSizeKey;
        });
        if (!pzInSize.length) return;
        var pzPrevColor = (item.passpartout && item.passpartout.color_label) || '';
        var pzPick = pzInSize.find((a) => String(a.color || '') === String(pzPrevColor)) || pzInSize[0];
        item.passpartout = item.passpartout || {};
        if (!item.passpartout.option_id || item.passpartout.option_id === 'none') item.passpartout.option_id = 'a3';
        item.passepartout_variant_id = pzPick.id;
        item.passpartout.color_hex = pzPick.swatch_color || '';
        item.passpartout.color_label = pzPick.color || '';
        item.passpartout.image = pzPick.swatch_image || '';
        item.passpartout.size = pzPick.size || '';
        item.passpartout.size_fit = pzPick.size_fit || '';
        item.passpartout.product_title = pzPick.product_title || '';
        // A different outer size means the frame must change size too.
        this._resyncFrameToPasse(item, pzProduct);
      } else if (kind === 'pass-color') {
        var passAddon = (this._passeVarMap && this._passeVarMap[String(value)]) || null;
        if (!passAddon) return;
        item.passpartout = item.passpartout || { option_id: 'a3' };
        if (item.passpartout.option_id === 'none') item.passpartout.option_id = 'a3';
        item.passepartout_variant_id = passAddon.id;
        item.passpartout.color_hex = passAddon.swatch_color || '';
        item.passpartout.color_label = passAddon.color || '';
        item.passpartout.image = passAddon.swatch_image || '';
        item.passpartout.size = passAddon.size || '';
        item.passpartout.size_fit = passAddon.size_fit || '';
        item.passpartout.product_title = passAddon.product_title || '';
        // A different matt may have a different outer size, so re-pick
        // any selected frame to keep it the right size.
        this._resyncFrameToPasse(item, this._productById(item.id));
      } else if (kind === 'frame-option') {
        item.frame = item.frame || {};
        item.frame.option_id = value;
        if (value === 'none') {
          item.frame.type = 'none';
          item.frame_variant_id = null;
        } else if (!item.frame_variant_id) {
          var firstF = (this._frameVarMap && Object.keys(this._frameVarMap)[0]) || null;
          if (firstF) {
            var firstFAddon = this._frameVarMap[firstF];
            item.frame_variant_id = firstFAddon.id;
            item.frame.type = firstFAddon.product_title || 'frame';
            item.frame.color_hex = firstFAddon.swatch_color || '';
            item.frame.color_label = firstFAddon.color || '';
            item.frame.image = firstFAddon.swatch_image || '';
            item.frame.product_title = firstFAddon.product_title || '';
          }
        }
      } else if (kind === 'frame-color') {
        var frameAddon = (this._frameVarMap && this._frameVarMap[String(value)]) || null;
        if (!frameAddon) return;
        item.frame = item.frame || { option_id: 'a3' };
        if ((item.frame.option_id || 'none') === 'none') item.frame.option_id = 'a3';
        item.frame_variant_id = frameAddon.id;
        item.frame.type = frameAddon.product_title || 'frame';
        item.frame.color_hex = frameAddon.swatch_color || '';
        item.frame.color_label = frameAddon.color || '';
        item.frame.image = frameAddon.swatch_image || '';
        item.frame.product_title = frameAddon.product_title || '';
      }
      // A size change clears the frame; re-assert that canvas stays framed.
      this._ensureCanvasDefaultFrame(item);
      writeJSON(LS_WALL, this.state.wall);
      this._renderWall();
      this._renderConfigure();
      this._updateSubtotal();
    }

    // ===========================================================
    // Share popup + URL state
    // ===========================================================

    // Build the visual passpartout/frame state for a wall item from a
    // saved addon variant id (used when hydrating a shared URL).
    // Returns `{ option_id: 'none' }` when no addon was picked.
    _buildAddonVisualState(kind, variantId) {
      if (!variantId) return { option_id: 'none' };
      var list = (this.addons && this.addons[kind]) || [];
      var addon = null;
      for (var i = 0; i < list.length; i++) {
        if (String(list[i].id) === String(variantId)) { addon = list[i]; break; }
      }
      if (!addon) return { option_id: 'none' };
      return {
        option_id: 'a3',
        // `image` (the frame mockup PNG) and `type` are required for the
        // frame to composite onto the wall — without them a shared frame
        // is invisible (a passepartout survives because it's drawn from a
        // colour SVG, not an image). _frameImage() reads item.frame.image.
        type: addon.product_title || 'frame',
        image: addon.swatch_image || '',
        color_hex: addon.swatch_color || '',
        color_label: addon.color || '',
        product_title: addon.product_title || '',
      };
    }

    _encodeWallState() {
      // Compact:
      //   [[handle, variant_id, x, y, passe_variant_id, frame_variant_id, artist], …]
      // The two addon ids preserve the picked passepartout + frame so
      // a shared URL recreates the exact bundle (0 = none). The 7th
      // element preserves the human-readable artist string resolved
      // by the card-side Liquid (plytix.product_artist → custom.artist
      // → vendor) so a restored wall doesn't fall back to the vendor
      // string on its own. Old 6-element tokens still decode fine.
      var arr = (this.state.wall || []).map(function (it) {
        var snap = it.product_snapshot || {};
        return [
          snap.handle || '',
          it.variant_id || 0,
          +(it.x || 0).toFixed(4),
          +(it.y || 0).toFixed(4),
          it.passepartout_variant_id ? Number(it.passepartout_variant_id) : 0,
          it.frame_variant_id ? Number(it.frame_variant_id) : 0,
          String(snap.artist || '').slice(0, 80),
        ];
      }).filter(function (e) { return e[0]; });
      if (!arr.length) return '';
      var json = JSON.stringify(arr);
      try {
        // Use base64url-safe encoding
        var b64 = btoa(unescape(encodeURIComponent(json)));
        return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
      } catch (e) {
        return '';
      }
    }

    _decodeWallState(token) {
      if (!token) return null;
      try {
        var b64 = token.replace(/-/g, '+').replace(/_/g, '/');
        while (b64.length % 4) b64 += '=';
        var json = decodeURIComponent(escape(atob(b64)));
        var arr = JSON.parse(json);
        if (!Array.isArray(arr)) return null;
        return arr;
      } catch (e) {
        return null;
      }
    }

    _buildShareUrl() {
      var token = this._encodeWallState();
      var url = new URL(window.location.href);
      url.searchParams.delete('wall');
      if (token) url.searchParams.set('wall', token);
      return url.toString();
    }

    _hydrateFromUrl() {
      var params = new URLSearchParams(window.location.search);
      var token = params.get('wall');
      if (!token) return Promise.resolve(false);
      var arr = this._decodeWallState(token);
      if (!arr || !arr.length) return Promise.resolve(false);
      var self = this;
      // Fetch each product by handle in parallel (memoized, localized
      // root, 404-vs-transient aware — see _fetchProductJson).
      var fetches = arr.map(function (entry) {
        return self._fetchProductJson(entry[0]);
      });
      return Promise.all(fetches).then(function (products) {
        var newWall = [];
        for (var i = 0; i < arr.length; i++) {
          var entry = arr[i];
          var p = products[i];
          if (!p) continue;
          // Sold-out products are excluded from the designer entirely —
          // a shared wall must not resurrect one the grid no longer offers.
          // (`!== false` matches the validator: only an explicit false
          // counts as sold out, so a missing flag errs towards keeping.)
          if (p.variants && p.variants.length && !p.variants.some(function (v) { return v.available !== false; })) continue;
          var variants = (p.variants || []).map(function (v) {
            var size = self._parseVariantSize(v.public_title || v.title || '');
            return {
              id: v.id,
              title: v.public_title || v.title || '',
              price: v.price, // Shopify .js returns price in cents
              available: v.available,
              width_cm: size ? size.width_cm : 21,
              height_cm: size ? size.height_cm : 29.7,
            };
          });
          // Prefer the artist that was embedded in the share token
          // (resolved by Liquid from plytix.product_artist → custom.artist
          // → vendor). Falls back to the vendor returned by /products/{handle}.js
          // for old tokens that don't include the 7th element.
          var encodedArtist = entry && entry.length > 6 ? String(entry[6] || '') : '';
          var artist = encodedArtist || (p.vendor || '');
          var img = '';
          if (p.images && p.images.length) {
            // The bare artwork is the LAST gallery image for most prints, but
            // the FIRST for Limited Edition (their gallery ends with
            // packaging / detail shots). Mirrors awd-product-card.liquid.
            var isLE = /limited\s*edition/i.test(p.type || p.product_type || '');
            var artImg = isLE ? p.images[0] : p.images[p.images.length - 1];
            img = (typeof artImg === 'string') ? artImg : (artImg && artImg.src) || '';
          }
          if (!img && p.featured_image) {
            img = (typeof p.featured_image === 'string') ? p.featured_image : (p.featured_image.src || '');
          }
          var snap = {
            id: p.id,
            title: p.title,
            artist: artist,
            image: img,
            price: p.price,
            handle: p.handle,
            url: '/products/' + p.handle,
            // Product type is needed downstream (e.g. Limited Edition →
            // sold as-is, no frame/passe). The product .js returns it as `type`.
            productType: p.type || p.product_type || '',
            variants: variants,
            // Client-rebuilt from /products/{handle}.js (variant titles),
            // which can't apply the server-side wall-object dimension fix.
            // Flag as a fallback so _hydrateWallProductData()'s authoritative
            // card data overrides it once fetched.
            __fromSnapshot: true,
          };
          self._productCache[p.id] = snap;
          // The shared token's variant may have sold out — or been
          // deleted and recreated with a new id — since the wall was
          // shared. Restore the item with the shared add-ons first, then
          // move it via _setItemVariant, which re-picks an equivalent
          // frame/passe-partout at the new size instead of dropping them.
          var sharedVid = entry[1] || (variants[0] && variants[0].id) || 0;
          var sharedVariant = variants.find(function (v) { return String(v.id) === String(sharedVid); });
          var variantSwapped = !sharedVariant || sharedVariant.available === false;
          var sharedItem = {
            uid: 'shared-' + i + '-' + Date.now(),
            id: p.id,
            variant_id: sharedVid,
            x: entry[2],
            y: entry[3],
            scale: 1,
            z: i,
            passepartout_variant_id: entry[4] || null,
            frame_variant_id: entry[5] || null,
            passpartout: self._buildAddonVisualState('passepartout', entry[4]),
            frame: self._buildAddonVisualState('frame', entry[5]),
            product_snapshot: snap,
          };
          newWall.push(sharedItem);
          if (variantSwapped) {
            self._setItemVariant(sharedItem, self._defaultVariantId(snap) || sharedVid);
          }
        }
        if (newWall.length) {
          self.state.wall = newWall;
          writeJSON(LS_WALL, newWall);
          return true;
        }
        return false;
      });
    }

    _openSharePopup() {
      if (!this.$sharePopup) return;
      this._renderSharePreview();
      this.$sharePopup.removeAttribute('hidden');
      document.body.style.overflow = 'hidden';
    }

    _closeSharePopup() {
      if (!this.$sharePopup) return;
      this.$sharePopup.setAttribute('hidden', '');
      document.body.style.overflow = '';
    }

    _renderSharePreview() {
      if (!this.$sharePopupPreview) return;
      // Clone the live canvas-stage so the preview shows the actual wall.
      this.$sharePopupPreview.innerHTML = '';
      var stage = this.$canvasStage;
      var canvas = this.$canvas;
      if (!stage || !canvas) return;
      var srcRect = canvas.getBoundingClientRect();
      var dstW = 476;
      var dstH = 371;
      var scale = Math.min(dstW / srcRect.width, dstH / srcRect.height);
      var wrap = document.createElement('div');
      wrap.style.width = srcRect.width + 'px';
      wrap.style.height = srcRect.height + 'px';
      wrap.style.transform = 'scale(' + scale + ')';
      wrap.style.transformOrigin = 'top left';
      wrap.style.position = 'relative';
      wrap.style.background = getComputedStyle(canvas).backgroundImage || getComputedStyle(canvas).backgroundColor;
      wrap.style.backgroundSize = 'cover';
      wrap.style.backgroundPosition = 'center';
      // Clone background layer
      var bgClone = this.$canvasBg ? this.$canvasBg.cloneNode(true) : null;
      if (bgClone) {
        bgClone.style.position = 'absolute';
        bgClone.style.inset = '0';
        wrap.appendChild(bgClone);
      }
      var stageClone = stage.cloneNode(true);
      stageClone.style.position = 'absolute';
      stageClone.style.inset = '0';
      // Strip all hover/interactive affordances from the clone so it
      // reads as a static snapshot, not a manipulable surface.
      stageClone.style.pointerEvents = 'none';
      stageClone.querySelectorAll('.awd__poster--selected, .is-selected, .is-hover').forEach(function (n) {
        n.classList.remove('awd__poster--selected', 'is-selected', 'is-hover');
      });
      stageClone.querySelectorAll('.awd__poster__controls, .awd__poster__btn, [data-awd-edit], [data-awd-delete]').forEach(function (n) {
        n.remove();
      });
      wrap.appendChild(stageClone);
      // Outer container with cropped scaled size
      var outer = document.createElement('div');
      outer.style.width = (srcRect.width * scale) + 'px';
      outer.style.height = (srcRect.height * scale) + 'px';
      outer.style.position = 'relative';
      outer.style.overflow = 'hidden';
      outer.appendChild(wrap);
      this.$sharePopupPreview.appendChild(outer);
    }

    _showShareToast(msg) {
      if (!this.$sharePopupToast) return;
      this.$sharePopupToast.textContent = msg;
      this.$sharePopupToast.removeAttribute('hidden');
      var self = this;
      clearTimeout(this._shareToastTimer);
      this._shareToastTimer = setTimeout(function () {
        self.$sharePopupToast.setAttribute('hidden', '');
      }, 1800);
    }

    _handleShareAction(action, btn) {
      var url = this._buildShareUrl();
      var subject = 'Check out my Art Wall';
      var body = 'I designed this Art Wall on The Poster Club:\n' + url;
      if (action === 'sms') {
        // iOS uses "&" after "?body=", Android uses "?body=". Use the
        // safer form: sms:?&body=... which works on iOS 8+ and Android.
        var sms = 'sms:?&body=' + encodeURIComponent(body);
        window.location.href = sms;
      } else if (action === 'mail') {
        var mailto = 'mailto:?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(body);
        window.location.href = mailto;
      } else if (action === 'copy') {
        var self = this;
        var done = function () { self._showShareToast('Link copied'); };
        var fail = function () { self._showShareToast('Press Cmd/Ctrl+C to copy'); };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(done).catch(function () {
            try {
              var ta = document.createElement('textarea');
              ta.value = url;
              ta.style.position = 'fixed';
              ta.style.opacity = '0';
              document.body.appendChild(ta);
              ta.select();
              document.execCommand('copy');
              document.body.removeChild(ta);
              done();
            } catch (e) { fail(); }
          });
        } else {
          try {
            var ta = document.createElement('textarea');
            ta.value = url;
            ta.style.position = 'fixed';
            ta.style.opacity = '0';
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
            done();
          } catch (e) { fail(); }
        }
      } else if (action === 'download') {
        this._downloadPdf(btn);
      }
    }

    _loadScript(src) {
      return new Promise(function (resolve, reject) {
        var existing = document.querySelector('script[data-awd-vendor="' + src + '"]');
        if (existing && existing.dataset.loaded === '1') return resolve();
        if (existing) {
          existing.addEventListener('load', function () { resolve(); });
          existing.addEventListener('error', function () { reject(new Error('load failed: ' + src)); });
          return;
        }
        var s = document.createElement('script');
        s.src = src;
        s.async = true;
        s.dataset.awdVendor = src;
        s.addEventListener('load', function () { s.dataset.loaded = '1'; resolve(); });
        s.addEventListener('error', function () { reject(new Error('load failed: ' + src)); });
        document.head.appendChild(s);
      });
    }

    _downloadPdf(btn) {
      var self = this;
      // Nothing to capture — bail early rather than producing a blank PDF.
      if (!this.state.wall || !this.state.wall.length) {
        this._showShareToast('Add a poster to your wall first');
        return;
      }
      if (btn) btn.setAttribute('disabled', 'disabled');
      var origText = btn ? btn.textContent : '';
      if (btn) btn.textContent = 'GENERATING…';

      // Resolve a vendor asset URL: prefer the theme helper, then a data
      // attribute on this element, then a <script> tag rendered by liquid.
      function assetUrl(file, attr, dsKey) {
        if (window.theme && window.theme.assetUrl) return window.theme.assetUrl(file);
        if (self.dataset[dsKey]) return self.dataset[dsKey];
        var el = document.querySelector('script[' + attr + ']');
        return el ? el.getAttribute(attr) : '';
      }
      var htmlToImageUrl = assetUrl('html-to-image.min.js', 'data-awd-htmltoimage-src', 'htmltoimageUrl');
      var jspdfUrl = assetUrl('jspdf.umd.min.js', 'data-awd-jspdf-src', 'jspdfUrl');

      var loads = [];
      if (!(window.htmlToImage && window.htmlToImage.toJpeg)) loads.push(this._loadScript(htmlToImageUrl));
      if (!(window.jspdf && window.jspdf.jsPDF)) loads.push(this._loadScript(jspdfUrl));

      // Capture the LIVE canvas (background + posters) exactly as shown.
      // html-to-image renders through the browser's own engine (SVG
      // <foreignObject>), so transforms, object-fit, shadows and the
      // background image all match the on-screen wall — the previous
      // html2canvas path mis-handled transforms / off-screen coordinates,
      // which is what wrecked the layout. The canvas subtree holds only
      // plain divs/imgs (no custom elements), so cloning it is safe.
      var node = self.$canvas;
      var rect = node.getBoundingClientRect();
      var w = Math.round(rect.width);
      var h = Math.round(rect.height);
      // Cap output so the long edge stays ~2400px (keeps the JPEG encode +
      // jsPDF.addImage off a multi-second main-thread block).
      var pixelRatio = Math.min(2, Math.max(1, 2400 / Math.max(w, h, 1)));

      // Elements inside the canvas that must NOT appear in the snapshot:
      // the per-poster edit/delete controls and the canvas footer
      // (CHANGE BACKGROUND / CLEAR WALL). html-to-image's `filter` skips a
      // node and its whole subtree when it returns false. (The bg / confirm
      // modals already carry the `hidden` attribute, but exclude them too
      // so an open one can never leak in.)
      var EXCLUDE = ['awd__poster__controls', 'awd__canvas-footer', 'awd__bg-modal', 'awd__confirm'];
      var excludeNode = function (n) {
        return !(n.classList && EXCLUDE.some(function (c) { return n.classList.contains(c); }));
      };
      // Drop any selection outline for the capture (it's a class on the
      // poster, not a separate node, so `filter` can't remove it).
      var deselected = [];
      node.querySelectorAll('.awd__poster--selected, .is-selected').forEach(function (n) {
        deselected.push(n);
        n.classList.remove('awd__poster--selected', 'is-selected');
      });
      var restore = function () {
        deselected.forEach(function (n) { n.classList.add('awd__poster--selected'); });
      };

      Promise.all(loads).then(function () {
        if (!(window.htmlToImage && window.htmlToImage.toJpeg) || !(window.jspdf && window.jspdf.jsPDF)) {
          throw new Error('PDF libraries failed to load');
        }
        var opts = {
          quality: 0.92,
          pixelRatio: pixelRatio,
          backgroundColor: '#ffffff',
          width: w,
          height: h,
          // `cacheBust` appends a unique query param to every image fetch,
          // which re-downloads the images on each warm-up pass below and so
          // defeats the warm-up (the point is to reuse the decoded image).
          // The wall's images are same-origin Shopify CDN assets that send
          // CORS headers, so caching them is safe and desirable here.
          cacheBust: false,
          filter: excludeNode,
        };
        // Safari/WebKit rasterizes the SVG <foreignObject> to canvas BEFORE
        // the embedded images have decoded, so the first pass(es) come back
        // blank — which is why the downloaded PDF was empty in Safari. A few
        // throwaway passes warm the decode cache; the final pass is complete.
        // Other browsers only need a single pass.
        var ua = (navigator && navigator.userAgent) || '';
        var isSafari = /^((?!chrome|android|crios|fxios|edg).)*safari/i.test(ua);
        var passCount = isSafari ? 3 : 1;
        var toJpeg = window.htmlToImage.toJpeg;
        function renderPasses(remaining) {
          return toJpeg(node, opts).then(function (url) {
            return remaining > 1 ? renderPasses(remaining - 1) : url;
          });
        }
        var render = renderPasses(passCount);
        // Hard timeout so a stalled image fetch can never freeze the UI.
        // Allow for the extra Safari warm-up passes.
        var timeoutId;
        var timeout = new Promise(function (_, reject) {
          timeoutId = setTimeout(function () { reject(new Error('PDF render timed out')); }, 30000);
        });
        return Promise.race([render, timeout]).then(function (dataUrl) {
          clearTimeout(timeoutId);
          return dataUrl;
        }, function (err) {
          clearTimeout(timeoutId);
          throw err;
        });
      }).then(function (dataUrl) {
        restore();
        var pdfW = Math.round(w * pixelRatio);
        var pdfH = Math.round(h * pixelRatio);
        var orientation = pdfW > pdfH ? 'landscape' : 'portrait';
        var pdf = new window.jspdf.jsPDF({ unit: 'px', format: [pdfW, pdfH], orientation: orientation });
        pdf.addImage(dataUrl, 'JPEG', 0, 0, pdfW, pdfH, undefined, 'FAST');
        pdf.save('my-art-wall.pdf');
        if (btn) {
          btn.removeAttribute('disabled');
          btn.textContent = origText || 'DOWNLOAD PDF';
        }
      }).catch(function (err) {
        restore();
        // eslint-disable-next-line no-console
        console.error('[awd] pdf generation failed', err);
        self._showShareToast('PDF generation failed');
        if (btn) {
          btn.removeAttribute('disabled');
          btn.textContent = origText || 'DOWNLOAD PDF';
        }
      });
    }
  }

  customElements.define('art-wall-designer', ArtWallDesigner);
})();

